(() => {
  "use strict";

  // The creative state belongs to exactly one School Store session. It is keyed by
  // that session, so a later school on the same browser cannot restore it, and any
  // state left by another session is removed rather than left to linger.
  const STORAGE_PREFIX = "spirit-ink-magic-preview-v3";
  const SCHOOL_SESSION_KEY = "spiritInkSchoolSession:v2";
  const schoolStoreSessionBinding = () => {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(SCHOOL_SESSION_KEY) || "null");
      const account = window.SpiritInkAccountConfig;
      if (account && (account.loggedIn !== true || parsed?.customer_gid !== account.customerGid
        || !(Date.parse(parsed?.session_expires_at) > Date.now()))) return null;
      const schoolId = String(parsed?.school_id || "").trim();
      const sessionId = String(parsed?.session_id || "").trim();
      return schoolId && sessionId ? { school_id: schoolId, session_id: sessionId } : null;
    } catch {
      return null;
    }
  };
  const magicPreviewStateKey = (binding) => `${STORAGE_PREFIX}::${binding.school_id}::${binding.session_id}`;
  // Removes every Magic Preview state on this browser except, optionally, the one
  // the current session owns. The unscoped legacy key matches the prefix too.
  const purgeMagicPreviewState = (exceptKey = null) => {
    try {
      for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
        const key = window.localStorage.key(index);
        if (key && key.startsWith(STORAGE_PREFIX) && key !== exceptKey) window.localStorage.removeItem(key);
      }
    } catch {
      // Storage may be unavailable; there is then nothing to leak.
    }
  };
  // 5 carries the two independent placements. 4 is still read: its single
  // placement is the front of the composition it becomes.
  const STORAGE_VERSION = 5;
  const READABLE_STORAGE_VERSIONS = Object.freeze([4, 5]);
  // Preview generation is deliberately unlimited: one School Store session
  // customises several products in a row. MAX_SAVED_VERSIONS is a localStorage
  // size guard only - it never refuses a generation, and it keeps the earliest
  // versions so the School Logo original stays available for "Revert to
  // original".
  const MAX_SAVED_VERSIONS = 60;
  const COMPACT_COLOUR_COUNT = 8;
  // Mirrors integrations/recraft-magic-preview/src/placement.js. The editor and
  // the production renderer must agree on this value exactly.
  const DEFAULT_PLACEMENT = Object.freeze({ offset_x: 0, offset_y: 0, scale: 0.9 });
  const FRONT = "front";
  const BACK = "back";
  const SIDES = Object.freeze([FRONT, BACK]);
  // The garments the placement-aware production registry authorises for back
  // printing. A product outside this list never offers a back placement, whatever
  // a catalogue or region file may later contain.
  const BACK_PRINT_PRODUCTS = Object.freeze([
    "adult-hoodie",
    "adult-sweatshirt",
    "adult-tee",
    "kids-hoodie",
    "kids-sweatshirt",
    "kids-tee",
    "tote-bag",
  ]);
  const otherSide = (side) => (side === BACK ? FRONT : BACK);
  const sideLabel = (side) => (side === BACK ? "Back" : "Front");
  const commerce = window.SpiritInkMagicPreviewCommerce;
  const approvedPreview = window.SpiritInkMagicPreviewApprovedPreview;
  // The canonical print geometry, byte-identical to the copy the renderer uses.
  const geometry = window.SpiritInkPrintGeometry;
  if (!commerce || !approvedPreview || !geometry) {
    console.error("Magic Preview browser dependencies are unavailable.");
    return;
  }
  const PLACEMENT_GEOMETRY_V1 = geometry.PLACEMENT_GEOMETRY_V1;
  const PLACEMENT_GEOMETRY_V2 = geometry.PLACEMENT_GEOMETRY_V2;
  let printRegions = null;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  // A placement with no recorded version was created before the supplier
  // geometry existed and is replayed under the legacy model, unchanged.
  const placementGeometryVersion = (value) => (
    Number(value?.placement_geometry_version) === PLACEMENT_GEOMETRY_V2
      ? PLACEMENT_GEOMETRY_V2
      : PLACEMENT_GEOMETRY_V1
  );
  const clonePlacement = (value) => ({
    offset_x: clamp(Number(value?.offset_x ?? DEFAULT_PLACEMENT.offset_x), -1, 1),
    offset_y: clamp(Number(value?.offset_y ?? DEFAULT_PLACEMENT.offset_y), -1, 1),
    scale: clamp(Number(value?.scale ?? DEFAULT_PLACEMENT.scale), 0.55, 1),
    placement_geometry_version: placementGeometryVersion(value),
  });

  // The supplier-safe region for one side of a product, used only when its
  // recorded product canvas matches the catalogue image the editor is drawing on.
  const regionFor = (item, side = FRONT) => {
    if (!printRegions || !item) return null;
    const region = printRegions.find((candidate) => candidate.product_id === item.product_id
      && (side === BACK ? candidate.placement_id === BACK : candidate.placement_id !== BACK));
    if (!region) return null;
    const canvasWidth = Number(item.print_area?.canvas_width) || 1024;
    const canvasHeight = Number(item.print_area?.canvas_height) || 1200;
    return region.canvas.width === canvasWidth && region.canvas.height === canvasHeight ? region : null;
  };

  // Print area the editor draws, in normalised product-image coordinates. A v2
  // placement uses the supplier-safe region; a legacy placement keeps the box
  // it was authored against so the editor never misrepresents it. A back
  // placement exists only under the supplier geometry, so it has no legacy box
  // to fall back to and is simply not drawn without its region.
  const printAreaFor = (item, version, side = FRONT) => {
    const region = side === BACK || placementGeometryVersion(version) === PLACEMENT_GEOMETRY_V2
      ? regionFor(item, side)
      : null;
    if (region) {
      return {
        x: region.pixels.x / region.canvas.width,
        y: region.pixels.y / region.canvas.height,
        width: region.pixels.width / region.canvas.width,
        height: region.pixels.height / region.canvas.height,
      };
    }
    return side === BACK ? null : item?.print_area?.normalized || null;
  };

  // The photograph the side is drawn on, and whether a back placement is
  // possible at all: it needs the supplier's own back rectangle and the rear
  // photograph, never the front pixels of either.
  const productImageFor = (item, side = FRONT) => (
    side === BACK ? item?.product_back_image || null : item?.product_image || null
  );
  const backSupported = (item) => Boolean(
    item
    && BACK_PRINT_PRODUCTS.includes(item.product_id)
    && typeof item.product_back_image === "string"
    && item.product_back_image
    && regionFor(item, BACK),
  );

  // Aspect ratio of the artwork actually on screen. Normalised artwork carries
  // its own content box, so this is the ratio of the visible design.
  const artworkRatio = (node) => {
    const width = Number(node?.naturalWidth);
    const height = Number(node?.naturalHeight);
    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 ? width / height : null;
  };

  // Initial fit for a NEW placement: the supplier-safe maximum this artwork can
  // hold at the recommended print resolution, never a fixed percentage.
  const initialPlacementFor = (item, artwork = { kind: "vector" }, side = FRONT) => {
    const region = regionFor(item, side);
    // Without a supplier region there is nothing new to derive from, so the
    // placement stays legacy and keeps the established default.
    if (!region) return { ...DEFAULT_PLACEMENT, placement_geometry_version: PLACEMENT_GEOMETRY_V1 };
    try {
      return {
        offset_x: 0,
        offset_y: 0,
        scale: geometry.initialScale(region, artwork),
        placement_geometry_version: PLACEMENT_GEOMETRY_V2,
      };
    } catch {
      return { ...DEFAULT_PLACEMENT, placement_geometry_version: PLACEMENT_GEOMETRY_V1 };
    }
  };
  const cloneMarker = (value) => {
    const x = Number(value?.marker_x);
    const y = Number(value?.marker_y);
    return Number.isFinite(x) && Number.isFinite(y) && x >= 0 && x <= 1 && y >= 0 && y <= 1
      ? { marker_x: x, marker_y: y }
      : { marker_x: null, marker_y: null };
  };
  const makeSessionId = () => {
    if (window.crypto?.randomUUID) return `mp_${window.crypto.randomUUID()}`;
    return `mp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  };
  const makeRequestId = (sessionId, iteration) => {
    const unique = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    return `${sessionId}_${iteration}_${unique}`;
  };
  const structuredSchoolName = (value) => {
    if (typeof value !== "string") return null;
    const normalized = value.trim();
    return normalized && normalized.length <= 200 && !/[\u0000-\u001f\u007f]/.test(normalized)
      ? normalized
      : null;
  };
  const customerError = (code, customerMessage, diagnostic = null) => {
    const error = new Error(code);
    error.customerMessage = customerMessage;
    error.diagnostic = diagnostic;
    return error;
  };
  const customerErrorMessage = (context, error, fallback) => {
    try {
      console.error(`Magic Preview ${context}`, {
        code: error?.message || "unknown_error",
        diagnostic: error?.diagnostic || null,
      });
    } catch {
      // Diagnostics must never interfere with the customer flow.
    }
    return typeof error?.customerMessage === "string" && error.customerMessage
      ? error.customerMessage
      : fallback;
  };

  function initRoot(root) {
    if (!root || root.dataset.mpReady) return;
    root.dataset.mpReady = "loading";

    const find = (selector) => root.querySelector(selector);
    const findAll = (selector) => [...root.querySelectorAll(selector)];
    const elements = {
      workspace: find(".mp-v2__workspace"),
      topbarTitle: find("[data-mp-topbar-title]"),
      topbarPrice: find("[data-mp-topbar-price]"),
      topbarBack: find("[data-mp-topbar-back]"),
      categorySection: find("[data-mp-category-section]"),
      categoryGrid: find("[data-mp-category-grid]"),
      catalogStatus: find("[data-mp-catalog-status]"),
      colourSection: find("[data-mp-colour-section]"),
      selectedProductWrap: find("[data-mp-selected-product-wrap]"),
      selectedProductImage: find("[data-mp-selected-product-image]"),
      selectedProductName: find("[data-mp-selected-product-name]"),
      selectedColourName: find("[data-mp-selected-colour-name]"),
      configurePrice: find("[data-mp-configure-price]"),
      changeCategory: find("[data-mp-change-category]"),
      swatches: find("[data-mp-swatches]"),
      designSection: find("[data-mp-design-section]"),
      designGrid: find("[data-mp-design-grid]"),
      designCancel: find("[data-mp-design-cancel]"),
      personaliseSection: find("[data-mp-personalise-section]"),
      personaliseHeading: find("[data-mp-personalise-heading]"),
      personaliseProduct: find("[data-mp-personalise-product]"),
      personaliseSide: find("[data-mp-personalise-side]"),
      personaliseDesignThumb: find("[data-mp-personalise-design-thumb]"),
      personaliseChange: find("[data-mp-personalise-change]"),
      startingWrap: find("[data-mp-starting-wrap]"),
      startingProductImage: find("[data-mp-starting-product-image]"),
      startingPrintArea: find("[data-mp-starting-print-area]"),
      startingImage: find("[data-mp-starting-image]"),
      startingCaption: find("[data-mp-starting-caption]"),
      initialForm: find("[data-mp-initial-form]"),
      schoolLogoModes: find("[data-mp-school-logo-modes]"),
      schoolLogoOriginal: find("[data-mp-school-logo-mode='original']"),
      schoolLogoAi: find("[data-mp-school-logo-mode='ai']"),
      initialPromptFields: find("[data-mp-initial-prompt-fields]"),
      initialPrompt: find("[data-mp-initial-prompt]"),
      initialLabel: find("[data-mp-initial-label]"),
      createButton: find("[data-mp-create]"),
      builderStatus: find("[data-mp-builder-status]"),
      reviewSection: find("[data-mp-review-section]"),
      previewStage: find("[data-mp-preview-stage]"),
      previewProduct: find("[data-mp-preview-product]"),
      printArea: find("[data-mp-print-area]"),
      previewArtwork: find("[data-mp-preview-artwork]"),
      artworkFrame: find("[data-mp-artwork-frame]"),
      serviceMockup: find("[data-mp-service-mockup]"),
      previewNote: find("[data-mp-preview-note]"),
      placementPanel: find("[data-mp-placement-panel]"),
      placementHint: find("[data-mp-placement-hint]"),
      resetPlacement: find("[data-mp-place='reset']"),
      startSideField: find("[data-mp-start-side-field]"),
      startSideButtons: findAll("[data-mp-start-side]"),
      printSidesRow: find("[data-mp-print-sides-row]"),
      printSidesSummary: find("[data-mp-print-sides-summary]"),
      printSidesToggle: find("[data-mp-print-sides-toggle]"),
      printSidesPanel: find("[data-mp-print-sides-panel]"),
      printSidesSelect: find("[data-mp-print-sides-select]"),
      sideTabs: find("[data-mp-side-tabs]"),
      sideTabButtons: findAll("[data-mp-side-tab]"),
      sidesStatus: find("[data-mp-sides-status]"),
      stageEmpty: find("[data-mp-stage-empty]"),
      stageEmptyLabel: find("[data-mp-stage-empty-label]"),
      sideEmpty: find("[data-mp-side-empty]"),
      copyDesign: find("[data-mp-copy-design]"),
      chooseSideDesign: find("[data-mp-choose-side-design]"),
      leaveSideBlank: find("[data-mp-leave-side-blank]"),
      copyToOtherSide: find("[data-mp-copy-to-other-side]"),
      copyConfirm: find("[data-mp-copy-confirm]"),
      copyConfirmCopy: find("[data-mp-copy-confirm-copy]"),
      copyConfirmAccept: find("[data-mp-copy-confirm-accept]"),
      copyConfirmCancel: find("[data-mp-copy-confirm-cancel]"),
      sideReview: find("[data-mp-side-review]"),
      reviewProduct: find("[data-mp-review-product]"),
      reviewPrice: find("[data-mp-review-price]"),
      reviewSwatches: find("[data-mp-review-swatches]"),
      reviewColourName: find("[data-mp-review-colour-name]"),
      sheetRows: findAll("[data-mp-open-sheet]"),
      sheetClosers: findAll("[data-mp-close-sheet]"),
      designRow: find("[data-mp-open-sheet='design']"),
      customiseRow: find("[data-mp-open-sheet='customise']"),
      rowColour: find("[data-mp-row-colour]"),
      rowDesign: find("[data-mp-row-design]"),
      rowCustomise: find("[data-mp-row-customise]"),
      designGroup: find("[data-mp-design-group]"),
      customiseGroup: find("[data-mp-customise-group]"),
      reviewDesign: find("[data-mp-review-design]"),
      reviewDesignThumb: find("[data-mp-review-design-thumb]"),
      reviewArtworkModeRow: find("[data-mp-review-artwork-mode-row]"),
      reviewArtworkMode: find("[data-mp-review-artwork-mode]"),
      versionRow: find("[data-mp-version-row]"),
      versionLabel: find("[data-mp-version-label]"),
      previousVersion: find("[data-mp-previous-version]"),
      nextVersion: find("[data-mp-next-version]"),
      useDesign: find("[data-mp-use-design]"),
      useOriginalLogo: find("[data-mp-use-original-logo]"),
      generateExactPreview: find("[data-mp-generate-exact-preview]"),
      generateProductMockup: find("[data-mp-generate-product-mockup]"),
      productMockupAction: find("[data-mp-product-mockup-action]"),
      productMockupPanel: find("[data-mp-product-mockup-panel]"),
      productMockupImage: find("[data-mp-product-mockup-image]"),
      productMockupCaption: find("[data-mp-product-mockup-caption]"),
      productMockupStatus: find("[data-mp-product-mockup-status]"),
      openMockup: find("[data-mp-open-mockup]"),
      lightbox: find("[data-mp-lightbox]"),
      lightboxImage: find("[data-mp-lightbox-image]"),
      lightboxCaption: find("[data-mp-lightbox-caption]"),
      closeLightbox: find("[data-mp-close-lightbox]"),
      lightboxDismiss: find("[data-mp-lightbox-dismiss]"),
      openRefine: find("[data-mp-open-refine]"),
      anotherDesign: find("[data-mp-another-design]"),
      anotherProduct: find("[data-mp-another-product]"),
      refinePanel: find("[data-mp-refine-panel]"),
      refineTitle: find("[data-mp-refine-title]"),
      refineCopy: find("[data-mp-refine-copy]"),
      closeRefine: find("[data-mp-close-refine]"),
      refineForm: find("[data-mp-refine-form]"),
      refinePrompt: find("[data-mp-refine-prompt]"),
      submitRefine: find("[data-mp-submit-refine]"),
      refineStatus: find("[data-mp-refine-status]"),
      purchase: find("[data-mp-purchase]"),
      backToEditor: find("[data-mp-back-to-editor]"),
      mappedPurchase: find("[data-mp-mapped-purchase]"),
      unmappedPurchase: find("[data-mp-unmapped-purchase]"),
      purchaseCopy: find("[data-mp-purchase-copy]"),
      purchaseHeading: find("[data-mp-purchase-heading]"),
      purchaseProduct: find("[data-mp-purchase-product]"),
      purchaseColour: find("[data-mp-purchase-colour]"),
      purchaseSidesRow: find("[data-mp-purchase-sides-row]"),
      purchaseSides: find("[data-mp-purchase-sides]"),
      priceRow: find("[data-mp-price-row]"),
      price: find("[data-mp-price]"),
      options: find("[data-mp-options]"),
      addToBag: find("[data-mp-add-to-bag]"),
      viewBag: find("[data-mp-view-bag]"),
      checkout: find("[data-mp-checkout]"),
      keepShopping: find("[data-mp-keep-shopping]"),
      bagSummary: find("[data-mp-bag-summary]"),
      bagLine: find("[data-mp-bag-line]"),
      cartStatus: find("[data-mp-cart-status]"),
      // Progress is drawn on the garment that is on screen: the starting preview
      // before the first version exists, the editor's stage afterwards.
      generating: findAll("[data-mp-generating]"),
      generatingTitles: findAll("[data-mp-generating-title]"),
      generatingCopies: findAll("[data-mp-generating-copy]"),
      announcer: find("[data-mp-announcer]"),
    };

    let catalog = null;
    let designs = [];
    let schoolLogoDesign = null;
    let selectedCategory = null;
    let selectedItem = null;
    let selectedDesign = null;
    let categoryColours = [];
    let showAllColours = false;
    let showAllReviewColours = false;
    let refineOpener = null;
    let sheetOpener = null;
    let selectedOptionKey = null;
    let cartPending = false;
    let productMockupPending = false;
    // Set when the design picker is opened from the editor, so it can be left
    // again without choosing anything.
    let designChangeFromEditor = false;
    // Versions the customer has moved or resized during this visit. Reset is
    // offered only for these.
    const movedVersions = new WeakSet();

    // Retail visits still start clean. In authenticated School Store mode we retain
    // presentation state only, and only for the session that created it; school-logo
    // authority is revalidated below before a stored logo version can be rendered or
    // submitted again.
    const sessionBinding = schoolStoreSessionBinding();
    const storageKey = sessionBinding ? magicPreviewStateKey(sessionBinding) : null;
    const clearStored = () => purgeMagicPreviewState();
    const readRestored = () => {
      if (typeof window.SpiritInkSchoolLogo?.getState !== "function" || !storageKey) {
        clearStored();
        return {};
      }
      // Whatever another session left behind goes now, not when it is next read.
      purgeMagicPreviewState(storageKey);
      try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "null");
        if (!parsed
          || typeof parsed !== "object"
          || !READABLE_STORAGE_VERSIONS.includes(parsed.storage_version)
          || parsed.school_id !== sessionBinding.school_id
          || parsed.school_session_id !== sessionBinding.session_id) {
          clearStored();
          return {};
        }
        return parsed;
      } catch {
        clearStored();
        return {};
      }
    };
    const restored = readRestored();
    // One placement engine, two independent placements. Everything creative
    // belongs to a side: its design, its prompts, its saved versions and the
    // placement each version carries. Product, colour, size, the session and the
    // preview allowance belong to the composition and stay shared.
    const restoreVersions = (value) => (Array.isArray(value) ? value.slice(0, MAX_SAVED_VERSIONS).map((version, index) => ({
        iteration: Number.isInteger(version?.iteration) ? version.iteration : index + 1,
        pipeline_iteration: Number.isInteger(version?.pipeline_iteration) ? version.pipeline_iteration : null,
        artwork_url: typeof version?.artwork_url === "string" ? version.artwork_url : "",
        mockup_url: typeof version?.mockup_url === "string" ? version.mockup_url : null,
        product_mockup_url: typeof version?.product_mockup_url === "string" ? version.product_mockup_url : null,
        product_mockup_product_id: typeof version?.product_mockup_product_id === "string"
          ? version.product_mockup_product_id
          : null,
        product_mockup_colour_id: typeof version?.product_mockup_colour_id === "string"
          ? version.product_mockup_colour_id
          : null,
        product_mockup_demographic: typeof version?.product_mockup_demographic === "string"
          ? version.product_mockup_demographic
          : null,
        preview_view: version?.preview_view === "exact" || version?.preview_view === "model"
          ? version.preview_view
          : null,
        school_name: structuredSchoolName(version?.school_name),
        request: typeof version?.request === "string" ? version.request : "",
        focus: typeof version?.focus === "string" ? version.focus : "other",
        source_kind: version?.source_kind === "school_logo" ? "school_logo" : "generated",
        school_logo_mode: version?.source_kind === "school_logo" && version?.school_logo_mode === "ai_customised"
          ? "ai_customised"
          : version?.source_kind === "school_logo" ? "original" : null,
        original_design_hash: typeof version?.original_design_hash === "string" ? version.original_design_hash : null,
        original_artwork_hash: typeof version?.original_artwork_hash === "string" ? version.original_artwork_hash : null,
        provider_calls: Number.isInteger(version?.provider_calls) ? version.provider_calls : null,
        design_id: typeof version?.design_id === "string" ? version.design_id : null,
        design_title: typeof version?.design_title === "string" ? version.design_title : "Personalised design",
        design_hash: typeof version?.design_hash === "string" ? version.design_hash : null,
        artwork_hash: typeof version?.artwork_hash === "string" ? version.artwork_hash : null,
        artwork_geometry_version: version?.artwork_geometry_version === undefined
          ? 0
          : version.artwork_geometry_version,
        ...clonePlacement(version),
        ...cloneMarker(version),
      })).filter((version) => version.artwork_url) : []);

    const restoreSide = (value, includedByDefault) => {
      const versions = restoreVersions(value?.versions);
      return {
        design_id: typeof value?.design_id === "string" ? value.design_id : null,
        school_logo_mode: value?.school_logo_mode === "ai" ? "ai" : "original",
        initial_prompt: typeof value?.initial_prompt === "string" ? value.initial_prompt : "",
        initial_focus: typeof value?.initial_focus === "string" ? value.initial_focus : "text",
        refine_prompt: typeof value?.refine_prompt === "string" ? value.refine_prompt : "",
        refine_focus: typeof value?.refine_focus === "string" ? value.refine_focus : "text",
        versions,
        active_version: versions.length
          ? clamp(Number.isInteger(value?.active_version) ? value.active_version : versions.length - 1, 0, versions.length - 1)
          : -1,
        // Whether this side is printed on the item being bought, which is not
        // the same question as whether it holds a design.
        included: typeof value?.included === "boolean" ? value.included : includedByDefault,
      };
    };

    // A state written before the second placement existed describes exactly one
    // side, and that side is the front.
    const restoredSides = restored.storage_version === STORAGE_VERSION ? restored.sides : { front: restored };
    const state = {
      session_id: typeof restored.session_id === "string" && restored.session_id ? restored.session_id : makeSessionId(),
      preview_count: Number.isInteger(restored.preview_count) && restored.preview_count > 0 ? restored.preview_count : 0,
      category_id: typeof restored.category_id === "string" ? restored.category_id : null,
      item_id: typeof restored.item_id === "string" ? restored.item_id : null,
      school_name: structuredSchoolName(restored.school_name)
        || structuredSchoolName(root.dataset.schoolName)
        || "",
      selected_side: restored.selected_side === BACK ? BACK : FRONT,
      sides: {
        front: restoreSide(restoredSides?.front, true),
        back: restoreSide(restoredSides?.back, false),
      },
      pending: false,
    };
    const sideState = (side = state.selected_side) => state.sides[side];
    // Every function written for a single placement keeps working on exactly one
    // side: the one being edited. Nothing reads across sides by accident, and
    // nothing has to be copied back and forth when the side changes.
    for (const field of [
      "design_id",
      "school_logo_mode",
      "initial_prompt",
      "initial_focus",
      "refine_prompt",
      "refine_focus",
      "versions",
      "active_version",
    ]) {
      Object.defineProperty(state, field, {
        get: () => sideState()[field],
        set: (value) => { sideState()[field] = value; },
      });
    }
    const interruptedRequest = Boolean(restored.pending);
    state.preview_count = SIDES.reduce(
      (count, side) => state.sides[side].versions.reduce((max, item) => Math.max(max, item.iteration), count),
      state.preview_count,
    );
    if (state.selected_side === BACK && state.sides[BACK].active_version < 0 && !state.sides[BACK].included) {
      state.selected_side = FRONT;
    }

    const currentVersion = () => state.active_version >= 0 ? state.versions[state.active_version] : null;
    const versionOf = (side) => {
      const record = state.sides[side];
      return record.active_version >= 0 ? record.versions[record.active_version] : null;
    };
    const sideHasArtwork = (side) => state.sides[side].active_version >= 0;
    // A side can be printed only on a product that prints it.
    const sideAvailable = (side) => side === FRONT || backSupported(selectedItem);
    const includedSides = () => SIDES.filter((side) => sideAvailable(side) && state.sides[side].included);
    const printedSides = () => includedSides().filter(sideHasArtwork);
    // Everything SI-MP-00 is asked about. If any of it changes while a request is
    // in flight, the answer is about a different composition and is discarded.
    const compositionSignature = () => JSON.stringify(includedSides().map((side) => {
      const version = versionOf(side);
      if (!version) return [side, null];
      const placement = clonePlacement(version);
      return [
        side,
        version.iteration,
        version.design_hash,
        version.artwork_hash,
        placement.offset_x,
        placement.offset_y,
        placement.scale,
        placement.placement_geometry_version,
      ];
    }));
    const identityMatches = (version, identity) => (
      version?.design_id === identity?.design_id
      && version?.design_hash === identity?.design_hash
      && version?.artwork_url === identity?.artwork_url
      && version?.artwork_hash === identity?.artwork_hash
      && version?.artwork_geometry_version === identity?.artwork_geometry_version
    );
    const discardStaleSchoolLogoVersions = (record = sideState()) => {
      const retained = record.versions.filter((version) => version.source_kind !== "school_logo");
      record.versions = retained;
      record.active_version = retained.length ? retained.length - 1 : -1;
    };
    // `school_name` is no longer collected in a second customer field. Preserve
    // it only when structured state or catalogue metadata already owns it; never
    // parse it out of the customer's free-text request.
    const schoolName = () => [
      currentVersion()?.school_name,
      selectedDesign?.school_name,
      selectedItem?.school_name,
      state.school_name,
      root.dataset.schoolName,
    ].map(structuredSchoolName).find(Boolean) || null;
    // Identity of the accepted artwork. Placement and school name are deliberately
    // not part of this: both are per-order print instructions carried as cart line
    // properties, and design_hash feeds the Magic Preview Product Key.
    const designHashForVersion = async (version) => {
      const identity = commerce.normalizeGeneratedArtworkIdentity({
        design_id: version.design_id,
        design_hash: version.design_hash,
        artwork_url: version.artwork_url,
        artwork_hash: version.artwork_hash,
        artwork_geometry_version: version.artwork_geometry_version,
      });
      return identity.design_hash;
    };
    // The original school logo is presentation version zero because preparing it
    // makes no AI/provider call and must not consume the customer's preview
    // allowance. SI-MP-00's commercial contract, however, deliberately starts at
    // iteration one. Adapt only this server-verified original at the submit
    // boundary; generated versions and ordinary designs keep their own iteration.
    const submissionIterationForVersion = (version) => (
      version?.source_kind === "school_logo"
      && version?.school_logo_mode === "original"
      && version?.provider_calls === 0
      && version?.pipeline_iteration === 0
      && version?.iteration === 0
        ? 1
        : version?.iteration
    );

    const save = () => {
      state.category_id = selectedCategory?.category_id || state.category_id;
      state.item_id = selectedItem?.id || state.item_id;
      state.design_id = selectedDesign?.id || state.design_id;
      state.school_name = schoolName() || "";
      state.initial_prompt = elements.initialPrompt.value;
      state.refine_prompt = elements.refinePrompt.value;
      if (!storageKey) return;
      try {
        window.localStorage.setItem(storageKey, JSON.stringify({
          storage_version: STORAGE_VERSION,
          school_id: sessionBinding.school_id,
          school_session_id: sessionBinding.session_id,
          session_id: state.session_id,
          preview_count: state.preview_count,
          category_id: state.category_id,
          item_id: state.item_id,
          school_name: state.school_name,
          selected_side: state.selected_side,
          sides: state.sides,
          pending: state.pending,
          saved_at: new Date().toISOString(),
        }));
      } catch {
        // The experience remains usable when storage is unavailable.
      }
    };

    const announce = (message) => {
      elements.announcer.textContent = "";
      window.setTimeout(() => { elements.announcer.textContent = message; }, 20);
    };
    const setStatus = (element, kind, message) => {
      element.hidden = !message;
      element.dataset.kind = kind || "info";
      element.textContent = message || "";
    };
    const scrollToElement = (element) => element.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
    // Written only when the photograph actually changes. The side render draws
    // the garment on every pass, and a no-op write is still a mutation the School
    // Store and retail observers would have to answer.
    const setImage = (image, wrapper, url, alt) => {
      const next = url || "";
      const label = alt || "";
      if (image.getAttribute("src") === next && image.getAttribute("alt") === label) return;
      wrapper?.classList.remove("is-broken");
      image.alt = label;
      image.onload = () => wrapper?.classList.remove("is-broken");
      image.onerror = () => wrapper?.classList.add("is-broken");
      image.src = next;
    };
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    // Written only when the value actually changes. Every one of these nodes sits
    // inside the School Store and retail observers' subtree, where a no-op write
    // is still a mutation they would have to answer.
    const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
    const setHidden = (node, value) => { if (node && node.hidden !== value) node.hidden = value; };
    const setDisabled = (node, value) => { if (node && node.disabled !== value) node.disabled = value; };
    const setPressed = (node, value) => {
      const next = String(Boolean(value));
      if (node && node.getAttribute("aria-pressed") !== next) node.setAttribute("aria-pressed", next);
    };
    const setStyle = (node, property, value) => {
      if (node && node.style.getPropertyValue(property) !== value) node.style.setProperty(property, value);
    };
    const positionArea = (node, area) => {
      if (!node || !area) return;
      setStyle(node, "left", `${area.x * 100}%`);
      setStyle(node, "top", `${area.y * 100}%`);
      setStyle(node, "width", `${area.width * 100}%`);
      setStyle(node, "height", `${area.height * 100}%`);
    };
    const setClass = (node, name, value) => {
      if (node && node.classList.contains(name) !== value) node.classList.toggle(name, value);
    };
    // Indicative only. The catalogue carries a price solely where the commerce
    // manifest has confirmed it; Shopify checkout remains the authority, and this
    // value is never sent to SI-MP-00 nor used to build the cart.
    const priceLabel = (item) => {
      const price = item?.indicative_price;
      const amount = typeof price?.amount === "string" ? price.amount : null;
      return amount && price.currency_code === "GBP" && price.status === "confirmed" ? `£${amount}` : "";
    };
    // A customised School Logo version keeps its full title in the version data.
    // On screen it is still the school's logo, with the kind of version beside it.
    const displayDesignTitle = (version) => (
      version?.source_kind === "school_logo"
        ? schoolLogoDesign?.title || "Your school logo"
        : version?.design_title || ""
    );
    const designImageFor = (version) => (
      version?.source_kind === "school_logo"
        ? schoolLogoDesign?.display_image
        : designs.find((design) => design.id === version?.design_id)?.display_image
    ) || "";

    // One step of the journey is on screen at a time. The data attribute only
    // drives layout, and sits outside every observer's attribute filter.
    const setView = (view) => {
      if (root.dataset.mpView !== view) root.dataset.mpView = view;
      setHidden(elements.categorySection, view !== "product");
      setHidden(elements.colourSection, view !== "configure");
      setHidden(elements.designSection, view !== "configure");
      setHidden(elements.personaliseSection, view !== "personalise");
      setHidden(elements.reviewSection, view !== "editor");
      closeSheet();
    };
    // The review replaces the editor's controls instead of stacking beneath them.
    const setPurchaseOpen = (open) => {
      setHidden(elements.purchase, !open);
      // On a phone the top bar's back button is the way back from review.
      const backLabel = open ? "Back to editing" : "Back";
      if (elements.topbarBack && elements.topbarBack.getAttribute("aria-label") !== backLabel) {
        elements.topbarBack.setAttribute("aria-label", backLabel);
      }
      if (open) {
        root.dataset.mpPurchaseOpen = "true";
        closeSheet();
      } else if (root.dataset.mpPurchaseOpen) {
        delete root.dataset.mpPurchaseOpen;
      }
    };

    // On a phone the editor's colour, design and customise controls open as
    // sheets over the lower part of the page, so the garment stays in view.
    function openSheet(name, opener = null) {
      if (state.pending) return;
      sheetOpener = opener;
      root.dataset.mpActiveSheet = name;
      elements.sheetRows.forEach((row) => row.setAttribute("aria-expanded", String(row.dataset.mpOpenSheet === name)));
      if (name === "customise" && currentVersion()) {
        openRefinement();
        return;
      }
      const sheet = find(`[data-mp-sheet="${name}"]`);
      window.setTimeout(() => sheet?.querySelector("[data-mp-close-sheet]")?.focus(), 40);
    }
    function closeSheet(returnFocus = false) {
      const name = root.dataset.mpActiveSheet;
      if (!name) return;
      delete root.dataset.mpActiveSheet;
      elements.sheetRows.forEach((row) => row.setAttribute("aria-expanded", "false"));
      // Closing the customise sheet puts its form away too.
      if (name === "customise") {
        setHidden(elements.refinePanel, true);
        root.classList.remove("is-refining");
      }
      if (returnFocus) sheetOpener?.focus();
      sheetOpener = null;
    }

    // The composition always prints at least one side, and never a side this
    // product cannot print.
    const normalizeSides = () => {
      if (!sideAvailable(BACK)) state.sides[BACK].included = false;
      if (!includedSides().length) state.sides[FRONT].included = true;
      if (!sideAvailable(state.selected_side) || !state.sides[state.selected_side].included) {
        state.selected_side = includedSides()[0] || FRONT;
      }
    };
    const printSidesValue = () => {
      const included = includedSides();
      return included.length === 2 ? "front_back" : included[0] || FRONT;
    };
    const printSidesLabel = (sides) => (
      sides.length === 2 ? "Front and back" : sides[0] === BACK ? "Back only" : "Front only"
    );
    // Stated beside the continuation, so what will be printed is never a surprise.
    const printSidesSummary = (sides) => (
      sides.length === 2
        ? "Printing on the front and back"
        : sides[0] === BACK ? "Printing on the back" : "Printing on the front"
    );
    // A printed side that still holds no design. While there is one, the
    // composition is not finished and the continuation path says so.
    const missingSide = () => includedSides().find((side) => !sideHasArtwork(side)) || null;

    // A printed side with no design is still drawn on its own photograph, with
    // the supplier's print area outlined, so which side is blank is obvious.
    const renderEmptyStage = (side) => {
      setHidden(elements.printArea, true);
      setHidden(elements.artworkFrame, true);
      setHidden(elements.serviceMockup, true);
      if (!selectedItem) return;
      setImage(
        elements.previewProduct,
        elements.previewStage,
        productImageFor(selectedItem, side),
        side === BACK
          ? `Back of the ${selectedItem.product_title} in ${selectedItem.colour_title}`
          : `${selectedItem.product_title} in ${selectedItem.colour_title}`,
      );
      const area = printAreaFor(selectedItem, { placement_geometry_version: PLACEMENT_GEOMETRY_V2 }, side);
      positionArea(elements.stageEmpty, area);
      setText(elements.stageEmptyLabel, `${sideLabel(side)} has no design yet`);
      setHidden(elements.stageEmpty, !area);
    };

    // Replacing a design the other side already holds is deliberate and asks
    // first. An empty destination is a plain one-time copy and needs no question.
    let copyConfirmPending = null;
    function closeCopyConfirm() {
      copyConfirmPending = null;
      setHidden(elements.copyConfirm, true);
    }
    function openCopyConfirm(from, to) {
      copyConfirmPending = { from, to };
      setText(elements.copyConfirmCopy, `Replace the ${sideLabel(to).toLowerCase()} design with this one?`);
      setText(elements.copyConfirmAccept, `Replace ${sideLabel(to).toLowerCase()} design`);
      setHidden(elements.copyConfirm, false);
      window.setTimeout(() => elements.copyConfirmAccept?.focus(), 30);
    }

    // A product that cannot print a back drops it from the composition rather
    // than keeping hidden state, and says so instead of changing silently.
    const noteUnsupportedBack = () => {
      if (sideAvailable(BACK)) return false;
      if (!state.sides[BACK].included && !sideHasArtwork(BACK)) return false;
      setStatus(
        elements.sidesStatus,
        "info",
        `The ${selectedItem?.product_title || "product"} prints on the front only. Your front design is unchanged.`,
      );
      return true;
    };

    // The one authoritative render of the side being edited. It settles the
    // composition first, then draws the garment, the placement, the side control,
    // the rail and every contextual action from that same side in one pass - so
    // what is on the canvas and what the controls say can never disagree.
    const renderSideView = () => {
      normalizeSides();
      const available = sideAvailable(BACK);
      const sides = includedSides();
      const dual = sides.length === 2;
      const side = state.selected_side;
      const other = otherSide(side);
      const hasArtwork = sideHasArtwork(side);
      const missing = missingSide();

      if (hasArtwork) renderPreview();
      else renderEmptyStage(side);

      // What is printed is the customer's intent, not a consequence of artwork. It
      // is summarised beside the continuation and only changed on request.
      setHidden(elements.printSidesRow, !available);
      setText(elements.printSidesSummary, printSidesSummary(sides));
      if (elements.printSidesSelect) {
        const value = printSidesValue();
        if (elements.printSidesSelect.value !== value) elements.printSidesSelect.value = value;
        setDisabled(elements.printSidesSelect, state.pending);
      }
      // Front | Back is the one side control, offered for every product with a back.
      setHidden(elements.sideTabs, !available);
      elements.sideTabButtons.forEach((button) => {
        setPressed(button, button.dataset.mpSideTab === side);
        setDisabled(button, state.pending);
      });

      setHidden(elements.previewStage, false);
      setHidden(elements.placementPanel, !hasArtwork);
      renderPlacementHelp();
      setHidden(elements.sideEmpty, hasArtwork);
      if (!hasArtwork) {
        const source = versionOf(other);
        setHidden(elements.copyDesign, !source);
        setText(elements.copyDesign, `Use ${sideLabel(other).toLowerCase()} design`);
        // With nothing to reuse, choosing a design is the way forward.
        setClass(elements.chooseSideDesign, "mp-v2__button", !source);
        setClass(elements.chooseSideDesign, "mp-v2__secondary", Boolean(source));
        setText(elements.chooseSideDesign, source ? "Choose another design" : "Choose a design");
        setText(elements.leaveSideBlank, `Leave ${sideLabel(side).toLowerCase()} blank`);
        setHidden(elements.leaveSideBlank, !dual);
      }

      // Nothing in the rail acts on, or speaks for, a design that is not there.
      setHidden(elements.designGroup, !hasArtwork);
      setHidden(elements.customiseGroup, !hasArtwork);
      setHidden(elements.designRow, !hasArtwork);
      setHidden(elements.customiseRow, !hasArtwork);
      if (!hasArtwork) {
        setHidden(elements.reviewArtworkModeRow, true);
        setHidden(elements.useOriginalLogo, true);
      }

      // The continuation path never implies a two-sided garment is finished while
      // one of its printed sides is still empty.
      if (missing && missing !== side) {
        setHidden(elements.useDesign, false);
        setText(elements.useDesign, `Add the ${sideLabel(missing).toLowerCase()} design`);
      } else if (missing) {
        setHidden(elements.useDesign, true);
      } else {
        setHidden(elements.useDesign, false);
        setText(elements.useDesign, "Review");
      }

      const canCopy = available && dual && hasArtwork;
      setHidden(elements.copyToOtherSide, !canCopy);
      if (canCopy) {
        setText(elements.copyToOtherSide, sideHasArtwork(other)
          ? `Copy this design to the ${sideLabel(other).toLowerCase()}`
          : `Use this design on the ${sideLabel(other).toLowerCase()} too`);
      }
      if (!canCopy || copyConfirmPending?.from !== side) closeCopyConfirm();

      // Before the first preview the customer can start on either side; only a
      // back-printable product is asked.
      setHidden(elements.startSideField, !available);
      elements.startSideButtons.forEach((button) => {
        setPressed(button, button.dataset.mpStartSide === side);
        setDisabled(button, state.pending);
      });
      updateSummary();
    };

    // Step 2 picks the side to start on. It includes that side, and gives up the
    // other one only while it is still empty, so it can neither drop a design the
    // customer has already made nor decide the final print configuration by itself.
    function setStartSide(value) {
      if (state.pending) return;
      const target = value === BACK && sideAvailable(BACK) ? BACK : FRONT;
      state.sides[target].included = true;
      if (!sideHasArtwork(otherSide(target))) state.sides[otherSide(target)].included = false;
      normalizeSides();
      copyConfirmPending = null;
      // The composition changed, so any option the customer had chosen for the
      // previous one is no longer the option for this one.
      selectedOptionKey = null;
      setPurchaseOpen(false);
      if (target !== state.selected_side) {
        selectSide(target);
      } else {
        renderSideView();
        save();
      }
      renderSelectedItem();
      announce(`You will start on the ${sideLabel(target).toLowerCase()}.`);
    }

    // Switching sides restores that side exactly as it was left. Nothing is
    // generated, and the other side is never written to.
    function selectSide(side, { announcement = null } = {}) {
      if (state.pending || !sideAvailable(side)) return;
      if (side !== state.selected_side) {
        // The prompts on screen belong to the side being left.
        save();
        state.selected_side = side;
        state.sides[side].included = true;
        selectedOptionKey = null;
        copyConfirmPending = null;
        setPurchaseOpen(false);
        elements.initialPrompt.value = state.initial_prompt;
        elements.refinePrompt.value = state.refine_prompt;
        closeRefinement(false);
        setStatus(elements.refineStatus, "", "");
        setStatus(elements.productMockupStatus, "", "");
        setStatus(elements.sidesStatus, "", "");
        selectedDesign = availableDesigns().find((design) => (
          design.id === state.design_id && design.enabled && design.generation_supported === true
        )) || null;
      }
      if (sideHasArtwork(side)) {
        showVersion(state.active_version);
      } else {
        renderSideView();
        updateSummary();
        updateActions();
        save();
      }
      if (announcement) announce(announcement);
    }

    function applyPrintSides(value) {
      if (state.pending) return;
      const requested = value === "front_back" ? SIDES : [value === BACK ? BACK : FRONT];
      SIDES.forEach((side) => {
        state.sides[side].included = requested.includes(side) && sideAvailable(side);
      });
      normalizeSides();
      selectedOptionKey = null;
      copyConfirmPending = null;
      setPurchaseOpen(false);
      setStatus(elements.sidesStatus, "", "");
      const sides = includedSides();
      // Editing follows the choice, and lands on the side that still needs a
      // design rather than on the one that already has one.
      const target = sides.find((side) => !sideHasArtwork(side))
        || (sides.includes(state.selected_side) ? state.selected_side : sides[0]);
      if (target !== state.selected_side) selectSide(target);
      else {
        renderSideView();
        save();
      }
      announce(sides.length === 2
        ? `Printing on the front and the back. You are editing the ${sideLabel(state.selected_side).toLowerCase()}.`
        : `Printing on the ${sideLabel(sides[0]).toLowerCase()} only.`);
    }

    // The same immutable artwork, placed again on the other side. The copy is a
    // separate placement from the moment it is made: it takes that side's own
    // supplier fit, and nothing links the two afterwards.
    function copyDesignToSide(fromSide, toSide) {
      if (state.pending || !sideAvailable(toSide)) return;
      const source = versionOf(fromSide);
      if (!source) return;
      closeCopyConfirm();
      const target = state.sides[toSide];
      target.versions = [...target.versions, {
        ...source,
        mockup_url: null,
        product_mockup_url: null,
        product_mockup_product_id: null,
        product_mockup_colour_id: null,
        product_mockup_demographic: null,
        preview_view: null,
        ...clonePlacement(initialPlacementFor(selectedItem, { kind: "vector" }, toSide)),
        ...cloneMarker(source),
      }].slice(-MAX_SAVED_VERSIONS);
      target.active_version = target.versions.length - 1;
      target.design_id = source.source_kind === "school_logo" ? "school-logo" : source.design_id;
      target.school_logo_mode = state.sides[fromSide].school_logo_mode;
      target.included = true;
      // Copying says nothing about the side it was copied from: a back-only
      // composition stays back-only even when its design came from the front.
      selectSide(toSide, {
        announcement: `The ${sideLabel(fromSide).toLowerCase()} design is on the ${sideLabel(toSide).toLowerCase()} as well. Moving or resizing it here does not change the ${sideLabel(fromSide).toLowerCase()}.`,
      });
    }

    const chooseDesignForSide = (side) => {
      if (state.pending || !sideAvailable(side)) return;
      state.sides[side].included = true;
      selectSide(side);
      showDesignSelection(true);
    };

    // Everything that names the current product, colour or design reads it from
    // here: the phone's top bar, the editor's rail and rows, and the personalise step.
    const updateSummary = () => {
      const price = priceLabel(selectedItem);
      setText(elements.topbarTitle, selectedItem?.product_title || selectedCategory?.product_title || "");
      setText(elements.topbarPrice, price);
      [elements.configurePrice, elements.reviewPrice].forEach((node) => {
        setText(node, price);
        setHidden(node, !price);
      });
      if (selectedItem) {
        setText(elements.reviewProduct, selectedItem.product_title);
        setText(elements.rowColour, selectedItem.colour_title);
        setText(elements.personaliseProduct, `${selectedItem.product_title} · ${selectedItem.colour_title}`);
        renderReviewColours();
      }
      setText(elements.rowDesign, displayDesignTitle(currentVersion()) || selectedDesign?.title || "");
      setText(elements.personaliseSide, sideLabel(state.selected_side));
      setHidden(elements.personaliseSide, !sideAvailable(BACK));
      setText(elements.personaliseHeading, selectedDesign?.title || "");
      if (selectedDesign?.display_image) {
        setImage(elements.personaliseDesignThumb, null, selectedDesign.display_image, "");
      }
    };
    const schoolLogoSelected = () => selectedDesign?.source_kind === "school_logo";
    const schoolLogoAiSelected = () => schoolLogoSelected() && state.school_logo_mode === "ai";
    const updatePersonaliseMode = () => {
      const schoolLogo = schoolLogoSelected();
      const aiMode = schoolLogoAiSelected();
      setHidden(elements.schoolLogoModes, !schoolLogo);
      elements.schoolLogoOriginal.checked = schoolLogo && !aiMode;
      elements.schoolLogoAi.checked = aiMode;
      setHidden(elements.initialPromptFields, schoolLogo && !aiMode);
      elements.initialPrompt.required = !schoolLogo || aiMode;
    };
    const updateActions = () => {
      const schoolLogo = schoolLogoSelected();
      const aiMode = schoolLogoAiSelected();
      const designReady = selectedDesign?.generation_supported === true;
      const needsAi = !schoolLogo || aiMode;
      setDisabled(elements.createButton, state.pending || !selectedItem?.preview_supported
        || (needsAi && !selectedItem?.generation_supported) || !designReady
        || (needsAi && !elements.initialPrompt.value.trim()));
      setDisabled(elements.submitRefine, state.pending || !designReady
        || !currentVersion() || !elements.refinePrompt.value.trim());
      updatePersonaliseMode();
    };

    // Built once. Later calls only move the selection, so choosing a colour never
    // rebuilds the product cards underneath the page.
    const renderCategories = () => {
      const cards = [...elements.categoryGrid.querySelectorAll(".mp-v2__category-card")];
      if (cards.length === catalog.categories.length) {
        cards.forEach((card, index) => {
          setPressed(card, selectedCategory?.category_id === catalog.categories[index].category_id);
        });
        return;
      }
      elements.categoryGrid.replaceChildren();
      catalog.categories.forEach((category) => {
        const button = element("button", "mp-v2__category-card");
        button.type = "button";
        setPressed(button, selectedCategory?.category_id === category.category_id);
        const imageWrap = element("span", "mp-v2__category-image mp-v2__image-wrap");
        const image = element("img");
        image.width = 640;
        image.height = 480;
        image.loading = "lazy";
        const fallback = element("span", "mp-v2__image-fallback", `${category.product_title} image unavailable`);
        imageWrap.append(image, fallback);
        setImage(image, imageWrap, category.representative_image, "");
        const copy = element("span", "mp-v2__card-copy");
        copy.append(element("span", "mp-v2__card-name", category.product_title));
        button.append(imageWrap, copy);
        button.addEventListener("click", () => selectCategory(category, true));
        elements.categoryGrid.append(button);
      });
      elements.categoryGrid.setAttribute("aria-busy", "false");
    };

    // A short row of colours with the rest one tap away. The chosen colour is always
    // in the short row, and its name is shown once, beside the label.
    const compactColours = (colours, expanded) => {
      if (expanded || colours.length <= COMPACT_COLOUR_COUNT) return colours;
      const first = colours.slice(0, COMPACT_COLOUR_COUNT);
      if (selectedItem && !first.some((item) => item.id === selectedItem.id)) {
        first[first.length - 1] = selectedItem;
      }
      return first;
    };
    const renderSwatchList = (container, colours, expanded, onToggle, onChoose) => {
      if (!container) return;
      const signature = `${selectedItem?.id || ""}|${expanded}|${colours.map((item) => item.id).join(",")}`;
      if (container.dataset.signature === signature) return;
      container.dataset.signature = signature;
      // Rebuilding the row must not drop keyboard focus back onto the page.
      const hadFocus = container.contains(document.activeElement);
      container.replaceChildren();
      compactColours(colours, expanded).forEach((item) => {
        const button = element("button", "mp-v2__swatch");
        button.type = "button";
        button.title = item.colour_title;
        button.setAttribute("aria-label", item.colour_title);
        setPressed(button, selectedItem?.id === item.id);
        const dot = element("span", "mp-v2__swatch-dot");
        dot.style.setProperty("--swatch", item.swatch || "#ddd");
        dot.setAttribute("aria-hidden", "true");
        button.append(dot);
        button.addEventListener("click", () => onChoose(item));
        container.append(button);
      });
      const hiddenCount = colours.length - COMPACT_COLOUR_COUNT;
      if (hiddenCount > 0) {
        const more = element("button", "mp-v2__link mp-v2__swatch-more", expanded ? "Fewer" : `+${hiddenCount}`);
        more.type = "button";
        more.setAttribute("aria-expanded", String(expanded));
        more.setAttribute("aria-label", expanded ? "Show fewer colours" : `Show ${hiddenCount} more colours`);
        more.addEventListener("click", () => {
          onToggle();
          container.querySelector(".mp-v2__swatch-more")?.focus();
        });
        container.append(more);
      }
      if (hadFocus) container.querySelector('[aria-pressed="true"]')?.focus();
    };
    const renderSwatches = () => renderSwatchList(
      elements.swatches,
      categoryColours,
      showAllColours,
      () => {
        showAllColours = !showAllColours;
        renderSwatches();
      },
      (item) => selectItem(item, true),
    );

    const renderStartingPreview = () => {
      if (!selectedItem || !selectedDesign) return;
      const side = state.selected_side;
      // A design chosen now becomes a new placement on the side being designed,
      // so the starting preview outlines the supplier-safe area that placement
      // will actually use, on the photograph of that side.
      const area = printAreaFor(selectedItem, { placement_geometry_version: PLACEMENT_GEOMETRY_V2 }, side)
        || {
          x: 0.32,
          y: 0.24,
          width: 0.36,
          height: 0.34,
        };
      const canvasWidth = Number(selectedItem.print_area?.canvas_width) || 1024;
      const canvasHeight = Number(selectedItem.print_area?.canvas_height) || 1200;
      elements.startingWrap.style.aspectRatio = `${canvasWidth} / ${canvasHeight}`;
      setImage(
        elements.startingProductImage,
        elements.startingWrap,
        productImageFor(selectedItem, side),
        side === BACK
          ? `Back of the ${selectedItem.product_title} in ${selectedItem.colour_title}`
          : `${selectedItem.product_title} in ${selectedItem.colour_title}`,
      );
      setImage(
        elements.startingImage,
        elements.startingWrap,
        selectedDesign.display_image,
        `${selectedDesign.title} original artwork`,
      );
      elements.startingPrintArea.style.left = `${clamp(Number(area.x), 0, 1) * 100}%`;
      elements.startingPrintArea.style.top = `${clamp(Number(area.y), 0, 1) * 100}%`;
      elements.startingPrintArea.style.width = `${clamp(Number(area.width), 0.05, 1) * 100}%`;
      elements.startingPrintArea.style.height = `${clamp(Number(area.height), 0.05, 1) * 100}%`;
      elements.startingCaption.textContent = side === BACK
        ? `${selectedDesign.title} preview on the back of the ${selectedItem.product_title}, ${selectedItem.colour_title}.`
        : `${selectedDesign.title} preview on ${selectedItem.product_title}, ${selectedItem.colour_title}.`;
    };

    const renderSelectedItem = () => {
      if (!selectedItem) return;
      // The colour step shows the side the editor is about to open on, so
      // choosing to start on the back shows the back of the garment here too.
      const side = state.selected_side;
      setImage(
        elements.selectedProductImage,
        elements.selectedProductWrap,
        productImageFor(selectedItem, side) || selectedItem.product_image,
        side === BACK && productImageFor(selectedItem, BACK)
          ? `Back of the ${selectedItem.product_title} in ${selectedItem.colour_title}`
          : `${selectedItem.product_title} in ${selectedItem.colour_title}`,
      );
      setText(elements.selectedProductName, selectedItem.product_title);
      setText(elements.selectedColourName, selectedItem.colour_title);
      renderSwatches();
      renderStartingPreview();
      updateSummary();
    };

    const availableDesigns = () => schoolLogoDesign ? [schoolLogoDesign, ...designs] : designs;
    const compatibleDesigns = () => availableDesigns().filter((design) =>
      design.enabled
      && design.generation_supported === true
      && design.compatible_product_categories.includes(selectedCategory?.category_id));
    const renderDesigns = () => {
      const filtered = compatibleDesigns();
      elements.designGrid.replaceChildren();
      if (!filtered.length) {
        elements.designGrid.append(element("p", "mp-v2__empty", "No designs are available for this product yet."));
        return;
      }
      filtered.forEach((design) => {
        const button = element("button", "mp-v2__design-card");
        button.type = "button";
        setPressed(button, selectedDesign?.id === design.id
          || (selectedDesign?.source_kind === "school_logo" && design.source_kind === "school_logo"));
        const imageWrap = element("span", "mp-v2__design-image mp-v2__image-wrap");
        const image = element("img");
        image.width = 600;
        image.height = 600;
        image.loading = "lazy";
        imageWrap.append(image, element("span", "mp-v2__image-fallback", `${design.title} image unavailable`));
        setImage(image, imageWrap, design.display_image, "");
        const copy = element("span", "mp-v2__card-copy");
        copy.append(element("span", "mp-v2__card-name", design.title));
        button.append(imageWrap, copy);
        button.addEventListener("click", () => selectDesign(design, true));
        elements.designGrid.append(button);
      });
    };

    function selectCategory(category, shouldScroll) {
      selectedOptionKey = null;
      selectedCategory = category;
      categoryColours = catalog.items.filter((item) => item.enabled && item.category_id === category.category_id);
      selectedItem = categoryColours.find((item) => item.id === state.item_id)
        || categoryColours.find((item) => item.colour_id === category.representative_colour_id)
        || categoryColours[0];
      if (selectedDesign && !selectedDesign.compatible_product_categories.includes(category.category_id)) selectedDesign = null;
      showAllColours = false;
      showAllReviewColours = false;
      copyConfirmPending = null;
      designChangeFromEditor = false;
      setHidden(elements.designCancel, true);
      setView("configure");
      // A product that does not print a back cannot keep one in its composition.
      if (!noteUnsupportedBack()) setStatus(elements.sidesStatus, "", "");
      renderSideView();
      renderCategories();
      renderSelectedItem();
      renderDesigns();
      updateActions();
      save();
      if (shouldScroll) {
        scrollToElement(elements.workspace || elements.colourSection);
        pushStep("design");
      }
    }

    function selectItem(item, showExistingPreview) {
      selectedOptionKey = null;
      selectedItem = item;
      state.item_id = item.id;
      copyConfirmPending = null;
      setPurchaseOpen(false);
      if (!noteUnsupportedBack()) setStatus(elements.sidesStatus, "", "");
      renderSideView();
      renderSelectedItem();
      renderCategories();
      save();
      if (showExistingPreview && printedSides().length) showReview(true);
      else announce(`${item.product_title}, ${item.colour_title} selected.`);
    }

    function selectDesign(design, shouldScroll) {
      if (design?.generation_supported !== true) return;
      const designChanged = Boolean(selectedDesign && selectedDesign.id !== design.id);
      if (designChanged) {
        // Artwork identity never crosses a design boundary. In particular, a
        // School Logo selection cannot inherit a standard Metaobject result,
        // mockup, prompt or placement from the prior design.
        state.versions = [];
        state.active_version = -1;
        elements.initialPrompt.value = "";
        elements.refinePrompt.value = "";
        setStatus(elements.productMockupStatus, "", "");
        closeRefinement(false);
      }
      selectedOptionKey = null;
      selectedDesign = design;
      state.design_id = design.id;
      state.school_logo_mode = design.source_kind === "school_logo" ? "original" : state.school_logo_mode;
      designChangeFromEditor = false;
      setPurchaseOpen(false);
      setView("personalise");
      renderStartingPreview();
      renderDesigns();
      updateSummary();
      updateActions();
      save();
      if (shouldScroll) {
        scrollToElement(elements.workspace || elements.personaliseSection);
        pushStep("personalise");
      }
      // On a touch screen the keyboard would cover the preview, so the prompt is
      // focused only where there is a fine pointer.
      if (!schoolLogoSelected() && window.matchMedia("(pointer: fine)").matches) {
        window.setTimeout(() => elements.initialPrompt.focus({ preventScroll: true }), 250);
      }
    }

    const editIntent = (focus, prompt) => ({
      preserve_elements: [
        "overall composition",
        "visual hierarchy",
        "typography character",
        "emblem structure",
        "number and position of major elements",
      ],
      requested_text_changes: focus === "text" ? prompt : null,
      requested_colour_changes: focus === "colour" ? prompt : null,
      requested_mascot_changes: focus === "mascot" ? prompt : null,
      other_changes: focus === "other" ? prompt : null,
      creative_freedom: "none",
      redesign_requested: false,
    });

    const setGenerating = (active) => {
      state.pending = active;
      if (active) {
        closeSheet();
        // The original logo is prepared rather than generated, so it is not given a wait time.
        const originalLogo = schoolLogoSelected() && state.school_logo_mode !== "ai";
        elements.generatingTitles.forEach((node) => setText(node, originalLogo ? "Preparing your logo…" : "Creating your preview…"));
        elements.generatingCopies.forEach((node) => setText(node, originalLogo ? "This takes a moment." : "Usually about 15 seconds."));
      }
      elements.generating.forEach((node) => setHidden(node, !active));
      renderSideView();
      root.setAttribute("aria-busy", String(active));
      updateActions();
      save();
    };

    const originalSchoolLogoVersion = () => state.versions.find((version) => (
      version.source_kind === "school_logo" && version.school_logo_mode === "original"
    )) || null;

    const buildOriginalSchoolLogoVersion = (data) => {
      if (data.source_kind !== "school_logo"
        || data.school_logo_mode !== "original"
        || data.provider_calls !== 0) {
        throw new Error("invalid_school_logo_source_response");
      }
      const identity = commerce.normalizeGeneratedArtworkIdentity(data);
      if (identity.artwork_geometry_version !== commerce.GENERATED_ARTWORK_GEOMETRY_VERSION
        || data.original_artwork_hash !== identity.artwork_hash
        || data.original_design_hash !== identity.design_hash) {
        throw new Error("invalid_school_logo_original_identity");
      }
      const placement = commerce.placementForGeometryTransition({
        previous_geometry_version: null,
        next_geometry_version: commerce.GENERATED_ARTWORK_GEOMETRY_VERSION,
        previous_placement: null,
      });
      return {
        iteration: 0,
        pipeline_iteration: 0,
        ...identity,
        source_kind: "school_logo",
        school_logo_mode: "original",
        original_design_hash: data.original_design_hash,
        original_artwork_hash: data.original_artwork_hash,
        provider_calls: 0,
        mockup_url: null,
        request: "",
        focus: "school_logo",
        school_name: schoolName(),
        design_title: selectedDesign.title,
        ...clonePlacement(placement),
        ...cloneMarker(null),
      };
    };

    const resolveOriginalSchoolLogoVersion = async () => {
      const existing = originalSchoolLogoVersion();
      if (existing) return existing;
      const data = await window.SpiritInkSchoolLogo.prepare({
        magic_preview_session_id: state.session_id,
        iteration: 1,
      });
      const version = buildOriginalSchoolLogoVersion(data);
      state.versions.unshift(version);
      return version;
    };

    async function generateSchoolLogoPreview() {
      const bridge = window.SpiritInkSchoolLogo;
      if (state.pending || !selectedItem || !selectedDesign || typeof bridge?.prepare !== "function") return;
      setStatus(elements.builderStatus, "", "");
      setGenerating(true);
      try {
        let version = originalSchoolLogoVersion();
        if (version) {
          const data = await bridge.prepare({ magic_preview_session_id: state.session_id, iteration: 1 });
          const refreshed = buildOriginalSchoolLogoVersion(data);
          if (!identityMatches(version, refreshed)) {
            state.versions = state.versions.filter((item) => item.source_kind !== "school_logo");
            state.versions.unshift(refreshed);
            version = refreshed;
          }
        } else {
          version = await resolveOriginalSchoolLogoVersion();
        }
        state.school_logo_mode = "original";
        state.active_version = state.versions.indexOf(version);
        sideState().included = true;
        elements.refinePrompt.value = "";
        closeRefinement(false);
        setGenerating(false);
        save();
        showReview(true);
        announce("Your logo preview is ready.");
      } catch (error) {
        setGenerating(false);
        setStatus(
          elements.builderStatus,
          "error",
          customerErrorMessage(
            "school logo preview failed",
            error,
            "We couldn’t prepare your school logo preview. Please try again.",
          ),
        );
        elements.builderStatus.focus?.();
      }
    }

    async function generateSchoolLogoAiPreview({ prompt, focus, refinement }) {
      const bridge = window.SpiritInkSchoolLogo;
      if (state.pending || !selectedItem || !selectedDesign
        || typeof bridge?.prepare !== "function" || typeof bridge?.customise !== "function") return;
      const cleanPrompt = prompt.trim();
      if (!cleanPrompt) return;
      const target = refinement ? elements.refineStatus : elements.builderStatus;
      setStatus(elements.builderStatus, "", "");
      setStatus(elements.refineStatus, "", "");
      setGenerating(true);
      try {
        const original = await resolveOriginalSchoolLogoVersion();
        const previous = currentVersion()?.source_kind === "school_logo" ? currentVersion() : original;
        const placement = commerce.placementForGeometryTransition({
          previous_geometry_version: previous.artwork_geometry_version,
          next_geometry_version: commerce.GENERATED_ARTWORK_GEOMETRY_VERSION,
          previous_placement: clonePlacement(previous),
        });
        const marker = cloneMarker(previous);
        const iteration = state.preview_count + 1;
        const data = await bridge.customise({
          magic_preview_session_id: state.session_id,
          iteration,
          request_id: makeRequestId(state.session_id, iteration),
          user_prompt: cleanPrompt,
          edit_intent: editIntent(focus, cleanPrompt),
          product_id: selectedItem.product_id,
          preview_mode: selectedItem.preview_mode,
          mockup_template_id: selectedItem.mockup_template_id,
          offset_x: placement.offset_x,
          offset_y: placement.offset_y,
          scale: placement.scale,
          marker_x: marker.marker_x,
          marker_y: marker.marker_y,
        });
        if (data.source_kind !== "school_logo"
          || data.school_logo_mode !== "ai_customised"
          || data.provider_calls !== 1
          || data.original_artwork_hash !== original.artwork_hash
          || data.original_design_hash !== original.design_hash) {
          throw new Error("invalid_school_logo_ai_response");
        }
        const identity = commerce.normalizeGeneratedArtworkIdentity(data);
        if (identity.artwork_geometry_version !== commerce.GENERATED_ARTWORK_GEOMETRY_VERSION
          || identity.design_id !== original.design_id
          || identity.artwork_hash === original.artwork_hash
          || identity.design_hash === original.design_hash) {
          throw new Error("invalid_school_logo_ai_identity");
        }
        const version = {
          iteration,
          pipeline_iteration: 2,
          ...identity,
          source_kind: "school_logo",
          school_logo_mode: "ai_customised",
          original_design_hash: data.original_design_hash,
          original_artwork_hash: data.original_artwork_hash,
          provider_calls: 1,
          mockup_url: null,
          request: cleanPrompt,
          focus,
          school_name: schoolName(),
          design_title: `${selectedDesign.title} · AI customised`,
          ...clonePlacement({
            ...initialPlacementFor(selectedItem),
            offset_x: data.offset_x,
            offset_y: data.offset_y,
          }),
          ...cloneMarker({ marker_x: data.marker_x, marker_y: data.marker_y }),
        };
        state.versions.push(version);
        state.preview_count = iteration;
        state.active_version = state.versions.length - 1;
        sideState().included = true;
        state.school_logo_mode = "ai";
        elements.initialPrompt.value = cleanPrompt;
        elements.refinePrompt.value = "";
        closeRefinement(false);
        setGenerating(false);
        save();
        showReview(true);
        announce("Your new logo version is ready.");
      } catch (error) {
        setGenerating(false);
        setStatus(
          target,
          "error",
          customerErrorMessage(
            "school logo AI customisation failed",
            error,
            "We couldn’t create a new version of your logo. Your original logo is unchanged.",
          ),
        );
        target.focus?.();
      }
    }

    async function generatePreview({ prompt, focus, refinement }) {
      if (state.pending || !selectedItem || !selectedDesign) return;
      if (schoolLogoSelected()) {
        if (state.school_logo_mode === "ai" || refinement) {
          await generateSchoolLogoAiPreview({ prompt, focus, refinement });
        } else if (!refinement) {
          await generateSchoolLogoPreview();
        }
        return;
      }
      if (selectedDesign.generation_supported !== true) {
        setStatus(
          refinement ? elements.refineStatus : elements.builderStatus,
          "error",
          "This design can’t be customised yet.",
        );
        return;
      }
      const cleanPrompt = prompt.trim();
      if (!cleanPrompt) return;
      const previous = refinement ? currentVersion() : null;
      let placement;
      try {
        placement = commerce.placementForGeometryTransition({
          previous_geometry_version: previous?.artwork_geometry_version,
          next_geometry_version: commerce.GENERATED_ARTWORK_GEOMETRY_VERSION,
          previous_placement: previous ? clonePlacement(previous) : null,
        });
      } catch (error) {
        const target = refinement ? elements.refineStatus : elements.builderStatus;
        setStatus(
          target,
          "error",
          customerErrorMessage(
            "could not prepare artwork placement",
            error,
            "We couldn’t prepare this preview. Please try again.",
          ),
        );
        return;
      }
      const marker = previous ? cloneMarker(previous) : cloneMarker(null);
      const usageIteration = state.preview_count + 1;
      const pipelineIteration = refinement
        ? Math.max(2, Number(previous?.pipeline_iteration ?? previous?.iteration ?? 1) + 1)
        : 1;
      const requestId = makeRequestId(state.session_id, usageIteration);
      const payload = {
        session_id: state.session_id,
        request_id: requestId,
        design_id: selectedDesign.id,
        design_title: selectedDesign.title,
        display_image_url: selectedDesign.display_image,
        source_image_url: selectedDesign.source_image,
        previous_result_url: previous?.artwork_url || null,
        user_prompt: cleanPrompt,
        edit_intent: editIntent(focus, cleanPrompt),
        iteration: pipelineIteration,
        product_id: selectedItem.product_id,
        preview_mode: selectedItem.preview_mode,
        mockup_template_id: selectedItem.mockup_template_id,
        offset_x: placement.offset_x,
        offset_y: placement.offset_y,
        scale: placement.scale,
        marker_x: marker.marker_x,
        marker_y: marker.marker_y,
      };
      setStatus(elements.builderStatus, "", "");
      setStatus(elements.refineStatus, "", "");
      setGenerating(true);
      try {
        const response = await fetch(root.dataset.apiEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.message || "We couldn’t create the preview. Please try again.");
        }
        const identity = commerce.normalizeGeneratedArtworkIdentity(data);
        if (identity.artwork_geometry_version !== commerce.GENERATED_ARTWORK_GEOMETRY_VERSION) {
          throw new Error("unsupported_generated_artwork_geometry_version");
        }
        if (identity.design_id !== selectedDesign.id) throw new Error("mismatched_generated_design_id");
        const version = {
          iteration: usageIteration,
          pipeline_iteration: data.iteration,
          ...identity,
          mockup_url: data.mockup_url || null,
          request: cleanPrompt,
          focus,
          school_name: structuredSchoolName(data.school_name) || schoolName(),
          design_id: selectedDesign.id,
          design_title: selectedDesign.title,
          ...clonePlacement({
            ...initialPlacementFor(selectedItem),
            offset_x: data.offset_x,
            offset_y: data.offset_y,
          }),
          ...cloneMarker({ marker_x: data.marker_x, marker_y: data.marker_y }),
        };
        state.versions.push(version);
        state.preview_count = usageIteration;
        state.active_version = state.versions.length - 1;
        sideState().included = true;
        elements.initialPrompt.value = cleanPrompt;
        elements.refinePrompt.value = "";
        closeRefinement(false);
        setGenerating(false);
        save();
        showReview(true);
        announce(`Preview ${usageIteration} is ready.`);
      } catch (error) {
        setGenerating(false);
        const target = refinement ? elements.refineStatus : elements.builderStatus;
        setStatus(
          target,
          "error",
          customerErrorMessage(
            "preview generation failed",
            error,
            "We couldn’t create the preview. Please try again.",
          ),
        );
        target.focus?.();
      }
    }

    const applyPrintArea = () => {
      positionArea(elements.printArea, printAreaFor(selectedItem, currentVersion(), state.selected_side));
    };
    // The artwork box inside the print area, as a percentage on each axis. One
    // calculation for the side being edited and for the approval previews of
    // every purchased side.
    const placementBoxFor = (item, version, side, ratio) => {
      const placement = clonePlacement(version);
      const region = regionFor(item, side);
      if (placement.placement_geometry_version === PLACEMENT_GEOMETRY_V2 && ratio && region) {
        // The artwork is placed directly against the supplier-safe area, by the
        // same calculation the production renderer runs. The fit is computed in
        // the print area's real millimetres — its aspect ratio decides which
        // dimension limits the artwork — and only then expressed as a
        // percentage of the print-area element on each axis.
        const area = { x: 0, y: 0, width: region.physical.width, height: region.physical.height };
        const rect = geometry.fit(area, ratio, placement, PLACEMENT_GEOMETRY_V2);
        return {
          left: `${(rect.x / area.width) * 100}%`,
          top: `${(rect.y / area.height) * 100}%`,
          width: `${(rect.width / area.width) * 100}%`,
          height: `${(rect.height / area.height) * 100}%`,
        };
      }
      // Legacy: the scaled box is positioned and the artwork meet-fits inside
      // it, which is exactly what these placements were authored against.
      const free = 1 - placement.scale;
      return {
        left: `${free * ((placement.offset_x + 1) / 2) * 100}%`,
        top: `${free * ((placement.offset_y + 1) / 2) * 100}%`,
        width: `${placement.scale * 100}%`,
        height: `${placement.scale * 100}%`,
      };
    };
    const applyPlacement = () => {
      const version = currentVersion();
      if (!version) return;
      const box = placementBoxFor(
        selectedItem,
        version,
        state.selected_side,
        artworkRatio(elements.previewArtwork),
      );
      [elements.previewArtwork, elements.artworkFrame].forEach((node) => {
        if (!node) return;
        setStyle(node, "left", box.left);
        setStyle(node, "top", box.top);
        setStyle(node, "width", box.width);
        setStyle(node, "height", box.height);
      });
      setHidden(elements.artworkFrame, false);
    };
    const serviceMockupUrl = () => {
      const version = currentVersion();
      // The PSD service templates are photographed front views, so a back
      // placement is always previewed through the catalogue overlay.
      if (state.selected_side === BACK) return null;
      if (!version || selectedItem?.preview_mode !== "service" || !selectedItem.mockup_template_id) return null;
      const endpoint = new URL(root.dataset.apiEndpoint, window.location.href);
      const url = new URL("/api/magic-preview/mockup", endpoint.origin);
      const placement = clonePlacement(version);
      url.searchParams.set("template", selectedItem.mockup_template_id);
      url.searchParams.set("artwork", version.artwork_url);
      url.searchParams.set("offset_x", String(placement.offset_x));
      url.searchParams.set("offset_y", String(placement.offset_y));
      url.searchParams.set("scale", String(placement.scale));
      if (placement.placement_geometry_version === PLACEMENT_GEOMETRY_V2) {
        url.searchParams.set("placement_geometry_version", String(PLACEMENT_GEOMETRY_V2));
      }
      return window.SpiritInkMagicPreviewProxy?.url(url.toString()) || url.toString();
    };

    const approvedPreviewUrl = (side = state.selected_side) => {
      const version = versionOf(side);
      if (!version || !selectedItem) return null;
      const placement = clonePlacement(version);
      const built = approvedPreview.buildApprovedPreviewUrl({
        backend_endpoint: root.dataset.apiEndpoint,
        page_url: window.location.href,
        product_id: selectedItem.product_id,
        colour_id: selectedItem.colour_id,
        artwork: version.artwork_url,
        offset_x: placement.offset_x,
        offset_y: placement.offset_y,
        scale: placement.scale,
      });
      // Appended here so the shared builder keeps producing byte-identical
      // legacy URLs for every placement that has no v2 geometry.
      if (!built) return built;
      const previewUrl = new URL(built);
      if (placement.placement_geometry_version === PLACEMENT_GEOMETRY_V2) {
        previewUrl.searchParams.set("placement_geometry_version", String(PLACEMENT_GEOMETRY_V2));
      }
      // A back placement is drawn on the rear photograph against the supplier's
      // own back rectangle, so the preview identity states the side it depicts.
      // A front preview keeps the exact URL shape it has always had.
      if (side === BACK) previewUrl.searchParams.set("side", BACK);
      // Canonical on purpose. This exact string is the signed approved_preview_url
      // in the SI-MP-00 contract and the cart property that production reads, and
      // both require the /api/magic-preview/approved-preview path. Proxying happens
      // where the preview is actually fetched for display, not here.
      return previewUrl.toString();
    };

    const productMockupEndpoint = () => {
      const endpoint = new URL(root.dataset.apiEndpoint, window.location.href);
      return new URL("/api/magic-preview/product-mockup", endpoint.origin).toString();
    };

    const modelPreviewDemographic = () => commerce.modelDemographicForProduct(selectedItem?.product_id);
    const productMockupSupports = (demographic) => {
      // Accessories use the normal product preview and deliberately have no
      // model demographic. Clothing must match a capability declared by the page;
      // child products never fall through to an adult request.
      if (demographic === "neutral") return false;
      const configured = String(root.dataset.productMockupDemographics || "adult child")
        .split(/[\s,]+/)
        .filter(Boolean);
      return configured.includes(demographic);
    };
    const updateProductMockupAvailability = () => {
      // The School Store keeps the deterministic exact preview as its own
      // always-available action. The model photo sits beside it and is the same
      // retail presentation feature, gated by exactly the same rules.
      const schoolLogo = currentVersion()?.source_kind === "school_logo";
      const back = state.selected_side === BACK;
      if (elements.generateExactPreview) {
        elements.generateExactPreview.hidden = !schoolLogo;
        elements.generateExactPreview.disabled = !schoolLogo;
      }
      // A model photo is a front view of the garment, so it is not offered for a
      // back placement rather than being taken of the wrong side.
      if (back) {
        setHidden(elements.generateProductMockup, true);
        setDisabled(elements.generateProductMockup, true);
        setStatus(elements.productMockupStatus, "", "");
        return null;
      }
      let demographic;
      try {
        demographic = modelPreviewDemographic();
      } catch (error) {
        elements.generateProductMockup.disabled = true;
        elements.generateProductMockup.textContent = "See it on a model";
        setStatus(
          elements.productMockupStatus,
          "error",
          customerErrorMessage(
            "model product family is unsupported",
            error,
            "A model preview isn’t available for this product yet.",
          ),
        );
        return null;
      }
      const supported = productMockupSupports(demographic);
      elements.generateProductMockup.hidden = demographic === "neutral";
      elements.generateProductMockup.disabled = !supported;
      elements.generateProductMockup.textContent = "See it on a model";
      if (!supported) {
        setStatus(elements.productMockupStatus, "", "");
      }
      return supported ? demographic : null;
    };

    // A model photo belongs to the exact canonical product, colour and model
    // demographic it was taken on, so it is only shown under that binding.
    const modelPhotoUrl = () => {
      const version = currentVersion();
      if (!version?.product_mockup_url) return null;
      let demographic;
      try {
        demographic = modelPreviewDemographic();
      } catch {
        return null;
      }
      const bindingMatches = Boolean(
        version.product_mockup_product_id === selectedItem?.product_id
        && version.product_mockup_colour_id === selectedItem?.colour_id
        && version.product_mockup_demographic === demographic,
      );
      return bindingMatches ? version.product_mockup_url : null;
    };

    // The exact preview is recomputed from the approved artwork and placement
    // every time it is shown, so a model photo can never stand in for it.
    const currentPreviewView = () => currentVersion()?.preview_view ?? null;
    const currentPreviewImageUrl = () => {
      const view = currentPreviewView();
      if (view === "model") return modelPhotoUrl();
      // Displaying it is a request, so it goes through the authenticated proxy.
      // The canonical URL stays the identity that is signed and stored.
      if (view === "exact") {
        const canonical = approvedPreviewUrl();
        return canonical ? (window.SpiritInkMagicPreviewProxy?.url(canonical) || canonical) : canonical;
      }
      return null;
    };

    const renderProductMockup = () => {
      const url = currentPreviewImageUrl();
      setHidden(elements.productMockupPanel, !url);
      if (!url) {
        if (elements.productMockupImage.hasAttribute("src")) elements.productMockupImage.removeAttribute("src");
        return;
      }
      const exact = currentPreviewView() === "exact";
      setImage(
        elements.productMockupImage,
        null,
        url,
        exact
          ? `Exact accepted logo on the ${selectedItem.product_title} in ${selectedItem.colour_title}`
          : `Your design on the ${selectedItem.product_title} in ${selectedItem.colour_title}, worn by a model`,
      );
      setText(elements.productMockupCaption, exact
        ? "Exact preview of your logo on this product."
        : "For visualisation only");
    };

    const openLightbox = () => {
      const url = currentPreviewImageUrl();
      if (!url || !elements.lightbox) return;
      elements.lightboxImage.src = url;
      elements.lightboxImage.alt = elements.productMockupImage.alt;
      elements.lightboxCaption.textContent = currentPreviewView() === "exact"
        ? `Your logo on the ${selectedItem.product_title}, ${selectedItem.colour_title}.`
        : `${selectedDesign?.title ?? "Your design"} on the ${selectedItem.product_title}, ${selectedItem.colour_title}. For visualisation only.`;
      elements.lightbox.hidden = false;
      document.body.style.overflow = "hidden";
      window.setTimeout(() => elements.closeLightbox?.focus(), 30);
    };

    const closeLightbox = () => {
      if (!elements.lightbox || elements.lightbox.hidden) return;
      elements.lightbox.hidden = true;
      elements.lightboxImage.removeAttribute("src");
      document.body.style.overflow = "";
      elements.openMockup?.focus();
    };

    // The editor's own colours. Changing colour keeps the generated artwork and
    // every saved version; only the garment underneath changes, so the customer
    // never has to walk back to product selection to try another one.
    const renderReviewColours = () => {
      if (!selectedItem) return;
      const colours = categoryColours.length
        ? categoryColours
        : (catalog?.items ?? []).filter((item) => item.enabled && item.category_id === selectedItem.category_id);
      renderSwatchList(
        elements.reviewSwatches,
        colours,
        showAllReviewColours,
        () => {
          showAllReviewColours = !showAllReviewColours;
          renderReviewColours();
        },
        (item) => changeReviewColour(item.id),
      );
      setText(elements.reviewColourName, selectedItem.colour_title);
    };

    function changeReviewColour(itemId) {
      const next = (categoryColours.length ? categoryColours : catalog?.items ?? [])
        .find((item) => item.id === itemId);
      if (!next || next.id === selectedItem?.id) return;
      selectedItem = next;
      state.item_id = next.id;
      selectedOptionKey = null;
      // A model photo belongs to the exact canonical product and colour it was
      // taken on, so never show it under a different garment label. Both sides
      // are photographed on the same garment, so both are checked.
      SIDES.forEach((side) => state.sides[side].versions.forEach((version) => {
        if (
          version.product_mockup_url
          && (
            version.product_mockup_product_id !== next.product_id
            || version.product_mockup_colour_id !== next.colour_id
          )
        ) {
          version.product_mockup_url = null;
          version.product_mockup_product_id = null;
          version.product_mockup_colour_id = null;
          version.product_mockup_demographic = null;
          if (version.preview_view === "model") {
            version.preview_view = version.source_kind === "school_logo" ? "exact" : null;
          }
        }
      }));
      setPurchaseOpen(false);
      setStatus(elements.productMockupStatus, "", "");
      renderSelectedItem();
      renderCategories();
      // The colour can change what this product prints, so the composition is
      // re-checked before the side on screen is drawn again.
      if (!noteUnsupportedBack()) setStatus(elements.sidesStatus, "", "");
      normalizeSides();
      if (sideHasArtwork(state.selected_side)) showVersion(state.active_version);
      else renderSideView();
      announce(`Colour changed to ${next.colour_title}.`);
    }

    const renderPreview = () => {
      const version = currentVersion();
      if (!version || !selectedItem) return;
      const side = state.selected_side;
      const back = side === BACK;
      setHidden(elements.stageEmpty, true);
      setHidden(elements.printArea, false);
      setImage(
        elements.previewProduct,
        elements.previewStage,
        productImageFor(selectedItem, side),
        back
          ? `Back of the ${selectedItem.product_title} in ${selectedItem.colour_title}`
          : `${selectedItem.product_title} in ${selectedItem.colour_title}`,
      );
      if (elements.previewArtwork.getAttribute("src") !== version.artwork_url) {
        elements.previewArtwork.src = version.artwork_url;
      }
      elements.previewArtwork.decode?.().then(applyPlacement).catch(() => {});
      // The preview only speaks when something went wrong with it.
      elements.previewArtwork.onerror = () => {
        setText(elements.previewNote, "The artwork image couldn’t load. Your design is still saved.");
        setHidden(elements.previewNote, false);
      };
      applyPrintArea();
      applyPlacement();
      const mockupUrl = serviceMockupUrl();
      if (mockupUrl) {
        setHidden(elements.serviceMockup, false);
        elements.serviceMockup.onload = () => {
          setHidden(elements.serviceMockup, false);
          setHidden(elements.previewNote, true);
        };
        elements.serviceMockup.onerror = () => {
          setHidden(elements.serviceMockup, true);
          setText(elements.previewNote, "The product photo couldn’t load, so a simpler preview is shown.");
          setHidden(elements.previewNote, false);
          announce("The product photo couldn’t load, so a simpler preview is shown.");
        };
        if (elements.serviceMockup.getAttribute("src") !== mockupUrl) elements.serviceMockup.src = mockupUrl;
      } else {
        setHidden(elements.serviceMockup, true);
        if (elements.serviceMockup.hasAttribute("src")) elements.serviceMockup.removeAttribute("src");
        setHidden(elements.previewNote, true);
      }
      renderProductMockup();
    };

    function showVersion(index) {
      // The side is settled before anything is written for it, so the version on
      // screen is always a version of the side the controls name.
      normalizeSides();
      if (!state.versions.length) {
        renderSideView();
        return;
      }
      state.active_version = clamp(index, 0, state.versions.length - 1);
      const version = currentVersion();
      const versionDesign = version.source_kind === "school_logo"
        ? schoolLogoDesign
        : designs.find((design) => design.id === version.design_id);
      if (versionDesign) selectedDesign = versionDesign;
      setText(elements.reviewDesign, displayDesignTitle(version));
      const designImage = designImageFor(version);
      if (designImage) setImage(elements.reviewDesignThumb, null, designImage, "");
      // Versions are only worth naming once there is more than one.
      const versionText = `Version ${state.active_version + 1} of ${state.versions.length}`;
      const multiple = state.versions.length > 1;
      setHidden(elements.versionRow, !multiple);
      setText(elements.versionLabel, versionText);
      setText(elements.rowCustomise, multiple ? versionText : "");
      setDisabled(elements.previousVersion, state.active_version <= 0);
      setDisabled(elements.nextVersion, state.active_version >= state.versions.length - 1);
      setStatus(elements.productMockupStatus, "", "");
      updateProductMockupAvailability();
      const schoolLogo = version.source_kind === "school_logo";
      if (schoolLogo) {
        const aiVersion = version.school_logo_mode === "ai_customised";
        state.school_logo_mode = aiVersion ? "ai" : "original";
        setHidden(elements.reviewArtworkModeRow, false);
        setText(elements.reviewArtworkMode, aiVersion ? "Customised version" : "Original logo");
        setText(elements.openRefine, aiVersion ? "Customise" : "Customise with AI");
        setHidden(elements.useOriginalLogo, !aiVersion || !originalSchoolLogoVersion());
      } else {
        setHidden(elements.reviewArtworkModeRow, true);
        setText(elements.reviewArtworkMode, "");
        setText(elements.openRefine, "Customise");
        setHidden(elements.useOriginalLogo, true);
      }
      setDisabled(elements.openRefine, false);
      setPurchaseOpen(false);
      renderSideView();
      updateActions();
      save();
    }

    function showReview(shouldScroll) {
      // At least one purchased side must hold a design; the side on screen may
      // still be the empty one the customer is about to fill.
      if (!selectedItem || !printedSides().length) return;
      designChangeFromEditor = false;
      setView("editor");
      if (sideHasArtwork(state.selected_side)) showVersion(state.active_version);
      else renderSideView();
      if (shouldScroll) scrollToElement(elements.workspace || elements.reviewSection);
      pushStep("review");
    }

    // The personalise step for the design already chosen, reached again from
    // the browser's back button.
    function showPersonalise() {
      if (!selectedItem || !selectedDesign) return;
      closeRefinement(false);
      setPurchaseOpen(false);
      setView("personalise");
      renderStartingPreview();
      updateSummary();
      updateActions();
      pushStep("personalise");
    }

    // Must match the clamp inside clonePlacement, otherwise resizing appears to stick.
    const MIN_SCALE = 0.55;
    const MAX_SCALE = 1;

    // A hint until the artwork has been moved, and a way back once it has.
    const renderPlacementHelp = () => {
      const version = currentVersion();
      const moved = Boolean(version && movedVersions.has(version));
      setHidden(elements.placementHint, moved);
      setHidden(elements.resetPlacement, !moved);
    };

    const commitPlacement = (placement, message) => {
      const version = currentVersion();
      if (!version) return;
      const placementChanged = placement.offset_x !== version.offset_x
        || placement.offset_y !== version.offset_y
        || placement.scale !== version.scale;
      Object.assign(version, placement);
      // Placement is a print instruction, not product identity, so an accepted
      // design and any chosen option survive a reposition. Only the model
      // photo is dropped, because it was rendered at the previous placement.
      if (placementChanged) {
        movedVersions.add(version);
        version.product_mockup_url = null;
        version.product_mockup_product_id = null;
        version.product_mockup_colour_id = null;
        version.product_mockup_demographic = null;
        if (version.preview_view === "model") {
          version.preview_view = version.source_kind === "school_logo" ? "exact" : null;
        }
      }
      renderPreview();
      renderPlacementHelp();
      save();
      if (message) announce(message);
    };
    const updatePlacement = (action) => {
      const version = currentVersion();
      if (!version) return;
      const placement = clonePlacement(version);
      if (action === "centre") { placement.offset_x = 0; placement.offset_y = 0; }
      // Reset returns a v2 placement to the supplier-derived initial fit of the
      // side it is on, and a legacy placement to the default it was authored
      // against.
      if (action === "reset") {
        Object.assign(
          placement,
          placement.placement_geometry_version === PLACEMENT_GEOMETRY_V2
            ? initialPlacementFor(selectedItem, { kind: "vector" }, state.selected_side)
            : DEFAULT_PLACEMENT,
        );
      }
      commitPlacement(placement, action === "reset" ? "Artwork reset." : "Artwork centred.");
      if (action === "reset") {
        // Back where it started, so the hint returns and focus stays on the artwork.
        movedVersions.delete(version);
        renderPlacementHelp();
        elements.previewArtwork.focus({ preventScroll: true });
      }
    };
    // Drag to move, wheel or pinch to resize. The artwork itself is the control,
    // so there is no button grid to read, aim at or explain.
    const movePlacement = (dxFraction, dyFraction) => {
      const version = currentVersion();
      if (!version) return;
      const placement = clonePlacement(version);
      const free = 1 - placement.scale;
      if (free <= 0.0001) return;
      placement.offset_x = clamp(placement.offset_x + (dxFraction * 2) / free, -1, 1);
      placement.offset_y = clamp(placement.offset_y + (dyFraction * 2) / free, -1, 1);
      commitPlacement(placement);
    };
    const zoomPlacement = (delta) => {
      const version = currentVersion();
      if (!version) return;
      const placement = clonePlacement(version);
      placement.scale = clamp(placement.scale + delta, MIN_SCALE, MAX_SCALE);
      commitPlacement(placement);
    };

    const pointers = new Map();
    let dragOrigin = null;
    let pinchStart = null;
    const areaRect = () => {
      const rect = elements.printArea.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 ? rect : null;
    };
    const pinchDistance = () => {
      const [a, b] = [...pointers.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const onPointerDown = (event) => {
      if (!currentVersion()) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      elements.previewArtwork.setPointerCapture?.(event.pointerId);
      if (pointers.size === 1) {
        dragOrigin = { x: event.clientX, y: event.clientY };
        root.classList.add("is-dragging");
      } else if (pointers.size === 2) {
        dragOrigin = null;
        pinchStart = { distance: pinchDistance(), scale: clonePlacement(currentVersion()).scale };
      }
    };
    const onPointerMove = (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const rect = areaRect();
      if (!rect) return;
      if (pointers.size >= 2 && pinchStart) {
        const distance = pinchDistance();
        if (pinchStart.distance > 0) {
          const version = currentVersion();
          if (!version) return;
          const placement = clonePlacement(version);
          placement.scale = clamp(pinchStart.scale * (distance / pinchStart.distance), MIN_SCALE, MAX_SCALE);
          commitPlacement(placement);
        }
        event.preventDefault();
        return;
      }
      if (!dragOrigin) return;
      const dx = (event.clientX - dragOrigin.x) / rect.width;
      const dy = (event.clientY - dragOrigin.y) / rect.height;
      dragOrigin = { x: event.clientX, y: event.clientY };
      movePlacement(dx, dy);
      event.preventDefault();
    };
    const onPointerUp = (event) => {
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinchStart = null;
      if (pointers.size === 0) {
        dragOrigin = null;
        root.classList.remove("is-dragging");
        announce("Artwork position updated.");
      }
    };
    // Corner handles resize. Distance from the artwork centre drives the scale, so any
    // corner behaves the same way and the gesture matches every image editor.
    let resizeStart = null;
    const artworkCentre = () => {
      const rect = elements.previewArtwork.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    };
    const onHandleDown = (event) => {
      const version = currentVersion();
      if (!version) return;
      event.preventDefault();
      event.stopPropagation();
      const centre = artworkCentre();
      const distance = Math.hypot(event.clientX - centre.x, event.clientY - centre.y);
      if (distance < 4) return;
      resizeStart = { distance, scale: clonePlacement(version).scale, centre };
      event.target.setPointerCapture?.(event.pointerId);
      root.classList.add("is-resizing");
    };
    const onHandleMove = (event) => {
      if (!resizeStart) return;
      const distance = Math.hypot(event.clientX - resizeStart.centre.x, event.clientY - resizeStart.centre.y);
      const version = currentVersion();
      if (!version) return;
      const placement = clonePlacement(version);
      placement.scale = clamp(resizeStart.scale * (distance / resizeStart.distance), MIN_SCALE, MAX_SCALE);
      commitPlacement(placement);
      event.preventDefault();
    };
    const onHandleUp = (event) => {
      if (!resizeStart) return;
      resizeStart = null;
      root.classList.remove("is-resizing");
      announce("Artwork size updated.");
      event.preventDefault();
    };
    const onArtworkKey = (event) => {
      const step = event.shiftKey ? 0.05 : 0.02;
      if (event.key === "ArrowLeft") movePlacement(-step, 0);
      else if (event.key === "ArrowRight") movePlacement(step, 0);
      else if (event.key === "ArrowUp") movePlacement(0, -step);
      else if (event.key === "ArrowDown") movePlacement(0, step);
      else if (event.key === "+" || event.key === "=") zoomPlacement(0.04);
      else if (event.key === "-" || event.key === "_") zoomPlacement(-0.04);
      else return;
      event.preventDefault();
    };

    function closeRefinement(returnFocus = true) {
      setHidden(elements.refinePanel, true);
      root.classList.remove("is-refining");
      if (root.dataset.mpActiveSheet === "customise") closeSheet();
      if (returnFocus && refineOpener) refineOpener.focus();
    }
    // Customising happens beside the garment, with the current version still on
    // the stage.
    const openRefinement = () => {
      if (!currentVersion()) return;
      refineOpener = document.activeElement;
      const schoolLogo = currentVersion().source_kind === "school_logo";
      if (schoolLogo) state.school_logo_mode = "ai";
      setText(elements.refineTitle, schoolLogo ? "How should we change your logo?" : "What would you like to change?");
      setText(elements.refineCopy, schoolLogo ? "Your original logo always stays unchanged." : "");
      setHidden(elements.refineCopy, !schoolLogo);
      setHidden(elements.refinePanel, false);
      root.classList.add("is-refining");
      setStatus(elements.refineStatus, "", "");
      updateActions();
      window.setTimeout(() => elements.refinePrompt.focus({ preventScroll: true }), 40);
    };
    const renderIndicativePrice = (item) => {
      const label = priceLabel(item);
      setText(elements.price, label);
      setHidden(elements.priceRow, !label);
    };

    // The approval state for every purchased side: each one drawn exactly as the
    // editor draws it, from the same placement the contract will carry.
    const renderSideReview = (sides) => {
      const container = elements.sideReview;
      if (!container) return;
      container.replaceChildren();
      const drawn = sides.filter((side) => versionOf(side));
      container.dataset.count = String(drawn.length);
      setHidden(container, !drawn.length);
      // A product that only ever prints its front has no side to name.
      const named = sideAvailable(BACK);
      drawn.forEach((side) => {
        const version = versionOf(side);
        const figure = element("figure", "mp-v2__side-review-item");
        const stage = element("span", "mp-v2__side-review-stage");
        const product = element("img", "mp-v2__side-review-product");
        product.width = 512;
        product.height = 600;
        product.loading = "lazy";
        product.alt = side === BACK
          ? `Back of the ${selectedItem.product_title} in ${selectedItem.colour_title}`
          : `${selectedItem.product_title} in ${selectedItem.colour_title}`;
        product.src = productImageFor(selectedItem, side) || "";
        const area = element("span", "mp-v2__side-review-area");
        const printArea = printAreaFor(selectedItem, version, side);
        if (printArea) {
          area.style.left = `${printArea.x * 100}%`;
          area.style.top = `${printArea.y * 100}%`;
          area.style.width = `${printArea.width * 100}%`;
          area.style.height = `${printArea.height * 100}%`;
        }
        const artwork = element("img", "mp-v2__side-review-artwork");
        artwork.alt = "";
        artwork.src = version.artwork_url;
        const place = () => {
          const box = placementBoxFor(selectedItem, version, side, artworkRatio(artwork));
          artwork.style.left = box.left;
          artwork.style.top = box.top;
          artwork.style.width = box.width;
          artwork.style.height = box.height;
        };
        artwork.decode?.().then(place).catch(() => {});
        place();
        area.append(artwork);
        stage.append(product, area);
        const caption = element("figcaption", "mp-v2__side-review-caption");
        const text = element("span", "mp-v2__side-review-text");
        if (named) text.append(element("span", "mp-v2__side-review-label", sideLabel(side)));
        text.append(element("span", "mp-v2__side-review-design", displayDesignTitle(version)));
        const edit = element("button", "mp-v2__link", "Edit");
        edit.type = "button";
        edit.setAttribute("aria-label", `Edit the ${sideLabel(side).toLowerCase()}`);
        edit.addEventListener("click", () => {
          setPurchaseOpen(false);
          selectSide(side, { announcement: `Editing the ${sideLabel(side).toLowerCase()}.` });
          scrollToElement(elements.workspace || elements.reviewSection);
        });
        caption.append(text, edit);
        figure.append(stage, caption);
        container.append(figure);
      });
    };

    const renderPurchaseDetails = (sides) => {
      setText(elements.purchaseProduct, selectedItem.product_title);
      setText(elements.purchaseColour, selectedItem.colour_title);
      setText(elements.purchaseSides, printSidesLabel(sides));
      setHidden(elements.purchaseSidesRow, !sideAvailable(BACK));
      renderIndicativePrice(selectedItem);
    };

    const renderPurchase = async () => {
      if (!selectedItem) return;
      normalizeSides();
      const sides = includedSides();
      // Every side that will be printed must actually hold a design before the
      // purchase opens, and the customer is taken to the one that does not.
      const missing = sides.find((side) => !sideHasArtwork(side));
      if (missing) {
        selectSide(missing);
        setStatus(
          elements.sidesStatus,
          "info",
          `Add a design to the ${sideLabel(missing).toLowerCase()} to continue.`,
        );
        scrollToElement(elements.workspace || elements.reviewSection);
        return;
      }
      const composition = sides.map((side) => ({ side, version: versionOf(side) }));
      // A composition is authored only under the current artwork and supplier
      // placement geometry. A restored legacy placement is still purchasable on
      // its own front, so it is refused here rather than reinterpreted.
      if (sides.includes(BACK) && !composition.every(({ version }) => (
        version.artwork_geometry_version === commerce.GENERATED_ARTWORK_GEOMETRY_VERSION
        && clonePlacement(version).placement_geometry_version === PLACEMENT_GEOMETRY_V2
      ))) {
        setStatus(
          elements.sidesStatus,
          "error",
          "This saved design is from an earlier version and can only be printed on the front. Create it again to print both sides.",
        );
        return;
      }
      setStatus(elements.sidesStatus, "", "");
      selectedOptionKey = null;
      setPurchaseOpen(true);
      elements.viewBag.hidden = true;
      if (elements.checkout) elements.checkout.hidden = true;
      if (elements.keepShopping) elements.keepShopping.hidden = true;
      if (elements.bagSummary) elements.bagSummary.hidden = true;
      setStatus(elements.cartStatus, "", "");
      renderSideReview(sides);
      renderPurchaseDetails(sides);
      scrollToElement(elements.workspace || elements.reviewSection);
      let purchaseOptions = [];
      try {
        // Both the explicit contract flag and a usable option set must agree before
        // any purchase path opens. Anything malformed collapses to "not purchasable".
        purchaseOptions = selectedItem.purchase_supported === true
          ? commerce.normalizeCatalogOptions(selectedItem.options).filter((option) => option.available)
          : [];
        if (purchaseOptions.length) {
          for (const entry of composition) await designHashForVersion(entry.version);
        }
      } catch {
        purchaseOptions = [];
      }
      if (!root.dataset.siMp00Endpoint || !purchaseOptions.length) {
        elements.mappedPurchase.hidden = true;
        elements.unmappedPurchase.hidden = false;
        announce("This product can’t be ordered online yet. Your design is saved.");
        return;
      }
      elements.unmappedPurchase.hidden = true;
      elements.mappedPurchase.hidden = false;
      setText(elements.purchaseCopy, "");
      setText(elements.purchaseHeading, "Review your design");
      setHidden(elements.purchaseHeading, false);

      const dimensions = commerce.optionDimensions(purchaseOptions);
      elements.options.replaceChildren();

      // A product with a single possible combination needs no control at all.
      if (purchaseOptions.length === 1) {
        selectedOptionKey = purchaseOptions[0].option_key;
        elements.options.hidden = true;
        elements.addToBag.disabled = false;
        return;
      }
      elements.options.hidden = false;

      // One radio group per contract dimension. For the common single-dimension
      // product this renders exactly the markup the size selector always used.
      const selection = {};
      const syncSelection = () => {
        let resolved = null;
        try {
          resolved = commerce.resolveOptionKey(purchaseOptions, selection);
        } catch {
          resolved = null;
        }
        selectedOptionKey = resolved;
        elements.addToBag.disabled = !resolved;
        setStatus(
          elements.cartStatus,
          resolved || dimensions.some((name) => !selection[name]) ? "" : "error",
          resolved || dimensions.some((name) => !selection[name])
            ? ""
            : "That combination is not available. Please choose another.",
        );
      };

      dimensions.forEach((dimension) => {
        const group = element("fieldset", "mp-v2__size-row");
        group.append(element("legend", "", dimension));
        commerce.optionChoicesForDimension(purchaseOptions, dimension).forEach(({ value, label: displayLabel }) => {
          const label = element("label", "mp-v2__size");
          const input = document.createElement("input");
          input.type = "radio";
          input.name = `mp-option-${root.id}-${dimension}`;
          input.value = value;
          input.addEventListener("change", () => {
            selection[dimension] = value;
            syncSelection();
          });
          label.append(input, element("span", "", displayLabel));
          group.append(label);
        });
        elements.options.append(group);
      });
      elements.addToBag.disabled = true;
    };

    function showExactProductPreview() {
      const version = currentVersion();
      if (!version || !selectedItem) return;
      if (!approvedPreviewUrl()) {
        setStatus(elements.productMockupStatus, "error", "We couldn’t prepare the exact product preview.");
        return;
      }
      version.preview_view = "exact";
      save();
      renderProductMockup();
      setStatus(elements.productMockupStatus, "", "");
      announce("Your exact product preview is ready.");
    }

    async function createProductMockup() {
      const version = currentVersion();
      if (productMockupPending || !version || !selectedItem) return;
      const demographic = updateProductMockupAvailability();
      if (!demographic) return;
      const requestedDemographic = demographic;
      productMockupPending = true;
      elements.generateProductMockup.disabled = true;
      elements.generateProductMockup.setAttribute("aria-busy", "true");
      elements.generateProductMockup.textContent = "Taking the photo…";
      setStatus(elements.productMockupStatus, "", "");

      const placement = clonePlacement(version);
      const requestId = `mp_mockup_${window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;
      const payload = {
        session_id: state.session_id,
        request_id: requestId,
        accepted_artwork_url: version.artwork_url,
        product_id: selectedItem.product_id,
        model_demographic: requestedDemographic,
        colour_id: selectedItem.colour_id,
        colour_title: selectedItem.colour_title,
        product_image_url: selectedItem.product_image,
        mockup_template_id: selectedItem.mockup_template_id || null,
        offset_x: placement.offset_x,
        offset_y: placement.offset_y,
        scale: placement.scale,
        ...(placement.placement_geometry_version === PLACEMENT_GEOMETRY_V2
          ? { placement_geometry_version: PLACEMENT_GEOMETRY_V2 }
          : {}),
      };

      try {
        const response = await fetch(productMockupEndpoint(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success || !data.mockup_url) {
          throw customerError(
            "product_mockup_request_failed",
            "We couldn't create the model preview. Please try again.",
            { status: response.status, error_code: data.error_code || null },
          );
        }
        if (
          (demographic === "child" && data.model_demographic !== "child")
          || (typeof data.model_demographic === "string" && data.model_demographic !== requestedDemographic)
        ) {
          throw customerError(
            "product_mockup_demographic_mismatch",
            "We couldn't create the correct model preview for this product.",
          );
        }
        version.product_mockup_url = data.mockup_url;
        version.product_mockup_product_id = selectedItem.product_id;
        version.product_mockup_colour_id = selectedItem.colour_id;
        version.product_mockup_demographic = demographic;
        version.preview_view = "model";
        save();
        renderProductMockup();
        setStatus(elements.productMockupStatus, "", "");
        announce("Your photo is ready.");
      } catch (error) {
        setStatus(
          elements.productMockupStatus,
          "error",
          customerErrorMessage(
            "model preview generation failed",
            error,
            "We couldn't create the model preview. Please try again.",
          ),
        );
      } finally {
        productMockupPending = false;
        elements.generateProductMockup.removeAttribute("aria-busy");
        updateProductMockupAvailability();
      }
    }

    // SI-MP-00 answers through Make, whose edge can replace a refused body with
    // its own HTML page. Anything that is not a JSON object is simply "no
    // contract data"; raw markup never reaches the customer.
    const readContractJson = async (response) => {
      const body = await response.text().catch(() => "");
      try {
        const parsed = JSON.parse(body);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch {
        return {};
      }
    };

    async function addToBag() {
      const sides = includedSides();
      const composition = sides.map((side) => ({ side, version: versionOf(side) }));
      if (cartPending || !selectedOptionKey || !selectedItem || composition.some((entry) => !entry.version)) return;
      // A composition that prints a back is provisioned under the placement-aware
      // contract; a front-only line keeps the contract it has always used.
      const useComposition = sides.includes(BACK);
      // The leading placement is the front whenever one is printed, and it is the
      // design identity SI-MP-00 echoes and records.
      const version = composition[0].version;
      const authoritativeSchoolName = schoolName();
      const submissionIteration = submissionIterationForVersion(version);
      cartPending = true;
      elements.addToBag.disabled = true;
      elements.addToBag.setAttribute("aria-busy", "true");
      elements.addToBag.textContent = "Preparing…";
      setStatus(elements.cartStatus, "", "");
      // Presentation only: lets the School Store list back what is being added.
      try {
        const leadingPreview = approvedPreviewUrl(composition[0].side);
        root.dispatchEvent(new CustomEvent("mp:purchase-summary", {
          detail: {
            product_title: selectedItem.product_title,
            colour_title: selectedItem.colour_title,
            design_title: displayDesignTitle(version),
            print_sides: sideAvailable(BACK) ? printSidesLabel(sides) : "",
            price: priceLabel(selectedItem),
            preview_url: leadingPreview
              ? window.SpiritInkMagicPreviewProxy?.url(leadingPreview) || leadingPreview
              : "",
          },
        }));
      } catch {
        // A summary that cannot be built never affects the purchase itself.
      }
      // SI-MP-00 answers for the configuration that was sent. If the customer
      // changes anything while that call is in flight, the answer is no longer
      // about what they are looking at, so it is discarded rather than added.
      const submitted = {
        item_id: selectedItem.id,
        option_key: selectedOptionKey,
        composition: compositionSignature(),
      };
      const configurationUnchanged = () => selectedItem?.id === submitted.item_id
        && selectedOptionKey === submitted.option_key
        && compositionSignature() === submitted.composition;
      const placement = clonePlacement(version);
      const properties = useComposition
        ? {
          "Personalised design": "Yes",
          ...(authoritativeSchoolName ? { "School name": authoritativeSchoolName } : {}),
          "Product colour": selectedItem.colour_title,
          "Print sides": printSidesLabel(sides),
          ...Object.fromEntries(composition.flatMap(({ side, version: sideVersion }) => [
            [`${sideLabel(side)} design`, sideVersion.design_title],
            ...(sideVersion.request ? [[`${sideLabel(side)} personalisation request`, sideVersion.request]] : []),
          ])),
          "_magic_preview_session_id": state.session_id,
          "_magic_preview_iteration": String(submissionIteration),
        }
        : {
          "Personalised design": "Yes",
          ...(authoritativeSchoolName ? { "School name": authoritativeSchoolName } : {}),
          "Personalisation request": version.request,
          "Product colour": selectedItem.colour_title,
          "Selected style": version.design_title,
          "_magic_preview_artwork_url": version.artwork_url,
          "_magic_preview_mockup_url": serviceMockupUrl() || "overlay-preview-no-static-url",
          "_magic_preview_session_id": state.session_id,
          "_magic_preview_iteration": String(submissionIteration),
          "_magic_preview_offset_x": placement.offset_x.toFixed(3),
          "_magic_preview_offset_y": placement.offset_y.toFixed(3),
          "_magic_preview_scale": placement.scale.toFixed(3),
          ...(placement.placement_geometry_version === PLACEMENT_GEOMETRY_V2
            ? { "_magic_preview_placement_geometry_version": String(PLACEMENT_GEOMETRY_V2) }
            : {}),
          "_magic_preview_template_id": selectedItem.mockup_template_id || "overlay",
        };
      try {
        const approvedPreviewSource = approvedPreviewUrl(composition[0].side);
        properties["_magic_preview_approved_preview_url"] = approvedPreviewSource;
        // The product page has to show every side that will be printed, so a
        // two-sided composition sends the trailing side's approved preview too.
        // A single-sided composition sends nothing extra and is unchanged.
        const trailingPreviewSource = composition.length > 1
          ? approvedPreviewUrl(composition[1].side)
          : null;
        if (trailingPreviewSource) {
          properties["_magic_preview_approved_preview_url_back"] = trailingPreviewSource;
        }
        const endpoint = new URL(root.dataset.siMp00Endpoint);
        const loopback = endpoint.protocol === "http:"
          && (endpoint.hostname === "127.0.0.1" || endpoint.hostname === "localhost" || endpoint.hostname === "[::1]");
        if (endpoint.protocol !== "https:" && !loopback) {
          throw customerError(
            "unsafe_si_mp_00_endpoint",
            "We couldn't connect to the bag service. Please try again later.",
          );
        }
        let contractRequest;
        if (useComposition) {
          const placements = [];
          for (const { side, version: sideVersion } of composition) {
            const sidePlacement = clonePlacement(sideVersion);
            placements.push({
              side,
              design_id: sideVersion.design_id,
              design_hash: await designHashForVersion(sideVersion),
              artwork_hash: sideVersion.artwork_hash,
              artwork_url: sideVersion.artwork_url,
              artwork_geometry_version: sideVersion.artwork_geometry_version,
              placement_geometry_version: sidePlacement.placement_geometry_version,
              offset_x: sidePlacement.offset_x,
              offset_y: sidePlacement.offset_y,
              scale: sidePlacement.scale,
            });
          }
          contractRequest = commerce.buildSiMp00PayloadV3({
            product_id: selectedItem.product_id,
            colour_id: selectedItem.colour_id,
            selected_option_key: selectedOptionKey,
            session_id: state.session_id,
            iteration: submissionIteration,
            approved_preview_url: approvedPreviewSource,
            approved_preview_url_back: trailingPreviewSource,
            key_algorithm_version: commerce.KEY_ALGORITHM_V3_VERSION,
            placements,
          });
        } else {
          const designHash = await designHashForVersion(version);
          contractRequest = commerce.buildSiMp00Payload({
            product_id: selectedItem.product_id,
            colour_id: selectedItem.colour_id,
            selected_option_key: selectedOptionKey,
            design_id: version.design_id,
            design_hash: designHash,
            artwork_hash: version.artwork_hash,
            artwork_geometry_version: version.artwork_geometry_version,
            session_id: state.session_id,
            iteration: submissionIteration,
            approved_preview_url: approvedPreviewSource,
          });
        }
        const contractResponse = await fetch(endpoint.toString(), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(contractRequest),
        });
        const contractData = await readContractJson(contractResponse);
        if (!contractResponse.ok) {
          throw customerError(
            "si_mp_00_request_failed",
            "We couldn't prepare this personalised item. Please try again.",
            { status: contractResponse.status, error_code: contractData.error_code || null },
          );
        }
        const trusted = useComposition
          ? commerce.validateSiMp00ResponseV3(contractData, contractRequest)
          : commerce.validateSiMp00Response(contractData, contractRequest);
        if (!configurationUnchanged()) {
          throw customerError(
            "purchase_configuration_changed",
            "Your choices changed while we were preparing this. Please add to bag again.",
          );
        }
        Object.assign(properties, useComposition
          ? {
            "_magic_preview_product_key": trusted.magic_preview_product_key,
            "_magic_preview_product_id": contractRequest.product_id,
            "_magic_preview_colour_id": contractRequest.colour_id,
            "_magic_preview_option_key": contractRequest.selected_option_key,
            ...commerce.buildCompositionCartProperties(contractRequest.placements),
          }
          : {
            "_magic_preview_product_key": trusted.magic_preview_product_key,
            "_magic_preview_product_id": contractRequest.product_id,
            "_magic_preview_colour_id": contractRequest.colour_id,
            "_magic_preview_option_key": contractRequest.selected_option_key,
            "_magic_preview_design_id": contractRequest.design_id,
            "_magic_preview_design_hash": contractRequest.design_hash,
            "_magic_preview_artwork_hash": contractRequest.artwork_hash,
            "_magic_preview_artwork_geometry_version": String(contractRequest.artwork_geometry_version),
          });
        elements.addToBag.textContent = "Adding…";
        const shopifyVariantId = Number(trusted.shopify_variant_id);
        const response = await fetch("/cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: shopifyVariantId, quantity: 1, properties }] }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          // In School Store mode the bridge answers this request itself and states
          // why the product was not attached. That reason is already customer-safe
          // copy about the School Store, so it is shown as-is rather than being
          // replaced by retail bag wording for a bag that was never used.
          if (data.si_school_store === true && typeof data.description === "string" && data.description) {
            throw customerError("school_store_attach_failed", data.description, { status: response.status });
          }
          // Shopify answers 422 when the variant cannot be sold. Say what that
          // means for the customer instead of repeating the raw wording.
          const soldOut = response.status === 422 && /sold out|unavailable/i.test(data.description || data.message || "");
          throw customerError(
            soldOut ? "shopify_variant_unavailable" : "shopify_cart_add_failed",
            soldOut
              ? "This size isn’t available to order online right now. Your design is saved — please contact Spirit Ink and we’ll place it for you."
              : "We couldn't add your item to the bag. Please try again.",
            { status: response.status },
          );
        }
        setStatus(elements.cartStatus, "success", "Added to your bag, with your design and placement attached.");
        await refreshBag();
        announce("Personalised item added to your bag.");
      } catch (error) {
        setStatus(
          elements.cartStatus,
          "error",
          customerErrorMessage(
            "add to bag failed",
            error,
            "We couldn't add your item to the bag. Please try again.",
          ),
        );
      } finally {
        cartPending = false;
        elements.addToBag.removeAttribute("aria-busy");
        elements.addToBag.disabled = !selectedOptionKey;
        elements.addToBag.textContent = "Add to Bag";
      }
    }

    // Mirrors what the storefront itself does after an add: read /cart.js and
    // hand the customer the same two exits Shopify gives them, View Bag and
    // Checkout. /checkout is Shopify's own hosted checkout, so nothing about
    // payment, tax or shipping is reimplemented here.
    async function refreshBag() {
      if (!elements.bagSummary) return;
      try {
        const response = await fetch("/cart.js", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error("cart_unavailable");
        const cart = await response.json();
        const count = Number(cart.item_count) || 0;
        if (count < 1) {
          elements.bagSummary.hidden = true;
          return;
        }
        const money = (cents) => {
          const amount = (Number(cents) || 0) / 100;
          try {
            return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
              style: "currency",
              currency: cart.currency || "GBP",
            }).format(amount);
          } catch {
            return `£${amount.toFixed(2)}`;
          }
        };
        elements.bagLine.textContent =
          `${count} ${count === 1 ? "item" : "items"} in your bag · ${money(cart.total_price)}`;
        elements.bagSummary.hidden = false;
        if (elements.checkout) elements.checkout.hidden = false;
        if (elements.viewBag) elements.viewBag.hidden = false;
        if (elements.keepShopping) elements.keepShopping.hidden = false;
        // Keep the theme's own cart bubble in step with what we just added.
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      } catch {
        // The item is in the bag even if the summary could not be read; still
        // offer the exits rather than stranding the customer on this step.
        elements.bagSummary.hidden = false;
        elements.bagLine.textContent = "Your item is in your bag.";
        if (elements.checkout) elements.checkout.hidden = false;
        if (elements.viewBag) elements.viewBag.hidden = false;
        if (elements.keepShopping) elements.keepShopping.hidden = false;
      }
    }

    // Each step becomes a history entry so the browser Back button walks the journey
    // instead of jumping off the page.
    let suppressHistory = false;
    const pushStep = (step) => {
      if (suppressHistory) return;
      if (window.history.state?.mpStep === step) return;
      window.history.pushState({ mpStep: step }, "");
    };
    const goToStep = (step) => {
      suppressHistory = true;
      try {
        if (step === "review" && printedSides().length && selectedItem) showReview(false);
        else if (step === "personalise" && selectedItem && selectedDesign) showPersonalise();
        else if (step === "design" && selectedItem) showDesignSelection();
        else showProductSelection();
      } finally {
        suppressHistory = false;
      }
    };
    window.addEventListener("popstate", (event) => goToStep(event.state?.mpStep || "product"));

    const showProductSelection = () => {
      closeRefinement(false);
      setPurchaseOpen(false);
      designChangeFromEditor = false;
      setView("product");
      scrollToElement(elements.workspace || elements.categorySection);
      pushStep("product");
    };
    // The product, colour and design choices. Opened from the editor, they can be
    // left again without choosing anything.
    const showDesignSelection = (fromEditor = false) => {
      if (!selectedItem) {
        showProductSelection();
        return;
      }
      closeRefinement(false);
      setPurchaseOpen(false);
      designChangeFromEditor = fromEditor === true && printedSides().length > 0;
      setHidden(elements.designCancel, !designChangeFromEditor);
      setView("configure");
      renderSelectedItem();
      renderDesigns();
      scrollToElement(elements.workspace || elements.colourSection);
      pushStep("design");
    };

    elements.changeCategory?.addEventListener("click", showProductSelection);
    elements.designCancel?.addEventListener("click", () => showReview(true));
    elements.personaliseChange?.addEventListener("click", () => showDesignSelection());
    // The phone's back button steps out of the current screen, one level at a time.
    elements.topbarBack?.addEventListener("click", () => {
      const view = root.dataset.mpView;
      if (view === "editor" && !elements.purchase.hidden) {
        setPurchaseOpen(false);
        scrollToElement(elements.workspace || elements.reviewSection);
      } else if (view === "editor" || view === "personalise") {
        showDesignSelection(view === "editor");
      } else {
        showProductSelection();
      }
    });
    elements.schoolLogoModes.addEventListener("change", (event) => {
      if (!schoolLogoSelected() || !event.target.matches("[data-mp-school-logo-mode]")) return;
      state.school_logo_mode = event.target.value === "ai" ? "ai" : "original";
      updateActions();
      save();
      if (state.school_logo_mode === "ai") window.setTimeout(() => elements.initialPrompt.focus(), 40);
      announce(state.school_logo_mode === "ai"
        ? "Customise with AI selected. Your original logo stays unchanged."
        : "Original logo selected.");
    });
    elements.initialPrompt.addEventListener("input", () => { updateActions(); save(); });
    elements.refinePrompt.addEventListener("input", () => { updateActions(); save(); });
    elements.initialForm.addEventListener("submit", (event) => {
      event.preventDefault();
      generatePreview({
        prompt: elements.initialPrompt.value,
        focus: "other",
        refinement: false,
      });
    });
    elements.refineForm.addEventListener("submit", (event) => {
      event.preventDefault();
      generatePreview({
        prompt: elements.refinePrompt.value,
        focus: "other",
        refinement: true,
      });
    });
    elements.previousVersion.addEventListener("click", () => {
      if (state.active_version <= 0) return;
      showVersion(state.active_version - 1);
      announce(`Showing version ${state.active_version + 1}.`);
    });
    elements.nextVersion?.addEventListener("click", () => {
      if (state.active_version >= state.versions.length - 1) return;
      showVersion(state.active_version + 1);
      announce("Showing version " + (state.active_version + 1) + ".");
    });
    // The one continuation path. While a printed side is still empty it takes the
    // customer to that side instead of opening a purchase for half a garment.
    elements.useDesign.addEventListener("click", () => {
      const missing = missingSide();
      if (missing && missing !== state.selected_side) {
        selectSide(missing, { announcement: `Choose the ${sideLabel(missing).toLowerCase()} design.` });
        scrollToElement(elements.reviewSection);
        return;
      }
      renderPurchase();
    });
    elements.printSidesSelect?.addEventListener("change", (event) => applyPrintSides(event.target.value));
    // What prints is summarised beside Review and only opened when asked for.
    elements.printSidesToggle?.addEventListener("click", () => {
      const open = elements.printSidesPanel.hidden;
      setHidden(elements.printSidesPanel, !open);
      elements.printSidesToggle.setAttribute("aria-expanded", String(open));
      if (open) window.setTimeout(() => elements.printSidesSelect?.focus(), 30);
    });
    elements.startSideButtons.forEach((button) => {
      button.addEventListener("click", () => setStartSide(button.dataset.mpStartSide));
    });
    elements.sideTabButtons.forEach((button) => {
      button.addEventListener("click", () => selectSide(
        button.dataset.mpSideTab,
        { announcement: `Editing the ${sideLabel(button.dataset.mpSideTab).toLowerCase()}.` },
      ));
      // Left and right move between the two views of the same garment, which is
      // what a segmented control is expected to do from the keyboard.
      button.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        const target = otherSide(button.dataset.mpSideTab === BACK ? BACK : FRONT);
        const next = elements.sideTabButtons.find((candidate) => candidate.dataset.mpSideTab === target);
        if (!next || next.disabled) return;
        event.preventDefault();
        next.focus();
        selectSide(target, { announcement: `Editing the ${sideLabel(target).toLowerCase()}.` });
      });
    });
    elements.copyDesign?.addEventListener("click", () => copyDesignToSide(otherSide(state.selected_side), state.selected_side));
    elements.chooseSideDesign?.addEventListener("click", () => chooseDesignForSide(state.selected_side));
    // Leaving a side blank prints only the other one; nothing it held is deleted.
    elements.leaveSideBlank?.addEventListener("click", () => applyPrintSides(otherSide(state.selected_side)));
    elements.copyToOtherSide?.addEventListener("click", () => {
      const from = state.selected_side;
      const to = otherSide(from);
      if (state.pending || !sideAvailable(to) || !sideHasArtwork(from)) return;
      if (sideHasArtwork(to)) openCopyConfirm(from, to);
      else copyDesignToSide(from, to);
    });
    elements.copyConfirmAccept?.addEventListener("click", () => {
      const pending = copyConfirmPending;
      closeCopyConfirm();
      if (pending) copyDesignToSide(pending.from, pending.to);
    });
    elements.copyConfirmCancel?.addEventListener("click", () => {
      closeCopyConfirm();
      elements.copyToOtherSide?.focus();
    });
    elements.useOriginalLogo.addEventListener("click", () => {
      const index = state.versions.findIndex((version) => (
        version.source_kind === "school_logo" && version.school_logo_mode === "original"
      ));
      if (index < 0) return;
      state.school_logo_mode = "original";
      showVersion(index);
      announce("Showing your original logo.");
    });
    elements.generateExactPreview?.addEventListener("click", showExactProductPreview);
    elements.generateProductMockup.addEventListener("click", createProductMockup);
    elements.openRefine.addEventListener("click", openRefinement);
    elements.closeRefine.addEventListener("click", () => closeRefinement(true));
    elements.refinePanel.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeRefinement(true);
    });
    elements.sheetRows.forEach((row) => row.addEventListener("click", () => openSheet(row.dataset.mpOpenSheet, row)));
    elements.sheetClosers.forEach((button) => button.addEventListener("click", () => closeSheet(true)));
    find("[data-mp-sheet-backdrop]")?.addEventListener("click", () => closeSheet(true));
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && root.dataset.mpActiveSheet) closeSheet(true);
    });
    findAll("[data-mp-place]").forEach((button) => button.addEventListener("click", () => updatePlacement(button.dataset.mpPlace)));
    elements.previewArtwork.addEventListener("pointerdown", onPointerDown);
    elements.previewArtwork.addEventListener("pointermove", onPointerMove);
    elements.previewArtwork.addEventListener("pointerup", onPointerUp);
    elements.previewArtwork.addEventListener("pointercancel", onPointerUp);
    elements.previewArtwork.addEventListener("keydown", onArtworkKey);
    findAll("[data-mp-handle]").forEach((handle) => {
      handle.addEventListener("pointerdown", onHandleDown);
      handle.addEventListener("pointermove", onHandleMove);
      handle.addEventListener("pointerup", onHandleUp);
      handle.addEventListener("pointercancel", onHandleUp);
    });
    elements.anotherDesign.addEventListener("click", () => showDesignSelection(true));
    elements.anotherProduct.addEventListener("click", showProductSelection);
    elements.addToBag.addEventListener("click", addToBag);
    elements.backToEditor?.addEventListener("click", () => {
      setPurchaseOpen(false);
      renderSideView();
      scrollToElement(elements.workspace || elements.reviewSection);
    });
    elements.openMockup?.addEventListener("click", openLightbox);
    elements.closeLightbox?.addEventListener("click", closeLightbox);
    elements.lightboxDismiss?.addEventListener("click", closeLightbox);
    elements.keepShopping?.addEventListener("click", () => {
      if (elements.bagSummary) elements.bagSummary.hidden = true;
      setPurchaseOpen(false);
      scrollToElement(elements.workspace || elements.reviewSection);
    });
    // The School Store starts its next product from the beginning. Everything
    // already added to the store stays where it is.
    root.addEventListener("mp:start-product", () => {
      if (state.pending) return;
      setStatus(elements.cartStatus, "", "");
      if (elements.bagSummary) elements.bagSummary.hidden = true;
      showProductSelection();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && elements.lightbox && !elements.lightbox.hidden) closeLightbox();
    });

    const loadJson = async (url) => {
      const response = await fetch(url, { credentials: "same-origin", cache: "no-store" });
      if (!response.ok) throw new Error("The Magic Preview catalogue could not be loaded.");
      return response.json();
    };

    const schoolLogoStatePromise = typeof window.SpiritInkSchoolLogo?.getState === "function"
      ? window.SpiritInkSchoolLogo.getState().catch(() => null)
      : Promise.resolve(null);

    const printRegionsPromise = root.dataset.printRegionsUrl
      ? loadJson(root.dataset.printRegionsUrl).catch(() => null)
      : Promise.resolve(null);

    Promise.all([
      loadJson(root.dataset.catalogUrl),
      loadJson(root.dataset.designsUrl),
      schoolLogoStatePromise,
      printRegionsPromise,
    ]).then(async ([catalogPayload, designPayload, schoolStoreState, printRegionPayload]) => {
      // Without the supplier regions the editor stays on legacy geometry
      // rather than guessing, so a failed asset never changes what prints.
      printRegions = printRegionPayload?.contract_version === "print-regions-v2"
        && Array.isArray(printRegionPayload.regions)
        ? printRegionPayload.regions
        : null;
      if (!Array.isArray(catalogPayload?.categories) || catalogPayload.categories.length !== 9
        || !Array.isArray(catalogPayload?.items) || catalogPayload.items.length !== 351) {
        throw new Error("The product catalogue is incomplete. Please try again later.");
      }
      if (!Array.isArray(designPayload?.designs)) {
        throw new Error("The designs could not be loaded.");
      }
      catalog = catalogPayload;
      designs = designPayload.designs.filter((design) => design.enabled);

      // SESSION_STATE is the authority on who this browser belongs to right now.
      // Restoring is allowed only when it names the same school and session the
      // stored state was written under; anything else starts clean.
      const authoritative = schoolStoreState?.success === true
        ? {
          school_id: String(schoolStoreState.school_id || "").trim(),
          session_id: String(schoolStoreState.session_id || "").trim(),
        }
        : null;
      if (!authoritative
        || !sessionBinding
        || authoritative.school_id !== sessionBinding.school_id
        || authoritative.session_id !== sessionBinding.session_id) {
        purgeMagicPreviewState();
        SIDES.forEach((side) => {
          const record = state.sides[side];
          record.versions = [];
          record.active_version = -1;
          record.design_id = null;
          record.initial_prompt = "";
          record.refine_prompt = "";
          record.school_logo_mode = "original";
          record.included = side === FRONT;
        });
        state.selected_side = FRONT;
        state.preview_count = 0;
        state.category_id = null;
        state.item_id = null;
        state.pending = false;
        state.school_name = structuredSchoolName(root.dataset.schoolName) || "";
      }
      if (schoolStoreState?.school_logo?.available === true) {
        let displayImage = "";
        try {
          const candidate = new URL(String(schoolStoreState.school_logo.source_image_url || ""));
          if (candidate.protocol === "https:"
            && candidate.hostname === "cdn.shopify.com"
            && candidate.pathname.startsWith("/s/files/1/0946/5842/9268/files/")) {
            displayImage = candidate.toString();
          }
        } catch {
          // An invalid display URL cannot become source authority and is not shown.
        }
        if (displayImage) {
          schoolLogoDesign = Object.freeze({
            id: "school-logo",
            title: "Your school logo",
            display_image: displayImage,
            source_image: null,
            source_kind: "school_logo",
            // Every catalogue category that accepts artwork accepts the school's own
            // logo. The printable region, supplier mapping and production registry
            // cover caps and beanies exactly as they cover the garments, so there is
            // nothing here to exclude.
            compatible_product_categories: catalog.categories.map((category) => category.category_id),
            tags: ["school-logo", "original-artwork"],
            enabled: true,
            generation_supported: true,
            generation_profile: null,
            output_format: "original",
            sort_order: -1,
            school_name: structuredSchoolName(schoolStoreState.school_name),
          });
          state.school_name = structuredSchoolName(schoolStoreState.school_name) || state.school_name;
        }
      }
      // A saved School Logo version is restorable only while the School Store
      // still authorises that exact artwork. Each side is re-authorised on its
      // own, so one side losing its logo never disturbs the other.
      const revalidateSchoolLogoVersions = async (record) => {
        const restoredSchoolLogoVersions = record.versions.filter((version) => version.source_kind === "school_logo");
        if (!restoredSchoolLogoVersions.length) {
          if (record.design_id === "school-logo" && !schoolLogoDesign) record.design_id = null;
          return;
        }
        const restoredActiveVersion = record.active_version >= 0 ? record.versions[record.active_version] : null;
        let authoritativeOriginal = null;
        try {
          if (!schoolLogoDesign || typeof window.SpiritInkSchoolLogo?.prepare !== "function") {
            throw new Error("school_logo_no_longer_authoritative");
          }
          const resolved = await window.SpiritInkSchoolLogo.prepare({
            magic_preview_session_id: state.session_id,
            iteration: 1,
          });
          if (resolved.source_kind !== "school_logo"
            || resolved.school_logo_mode !== "original"
            || resolved.provider_calls !== 0) {
            throw new Error("invalid_school_logo_source_response");
          }
          authoritativeOriginal = {
            ...commerce.normalizeGeneratedArtworkIdentity(resolved),
            original_design_hash: resolved.original_design_hash,
            original_artwork_hash: resolved.original_artwork_hash,
          };
        } catch {
          authoritativeOriginal = null;
        }
        if (!authoritativeOriginal) {
          discardStaleSchoolLogoVersions(record);
          record.design_id = schoolLogoDesign ? "school-logo" : null;
          return;
        }
        const originalVersion = restoredSchoolLogoVersions.find((version) => (
          version.school_logo_mode === "original"
          && identityMatches(version, authoritativeOriginal)
          && version.original_artwork_hash === authoritativeOriginal.artwork_hash
          && version.original_design_hash === authoritativeOriginal.design_hash
        ));
        const validatedSchoolVersions = originalVersion ? [originalVersion] : [];
        if (originalVersion && typeof window.SpiritInkSchoolLogo?.validateDerivative === "function") {
          for (const version of restoredSchoolLogoVersions) {
            if (version.school_logo_mode !== "ai_customised"
              || version.original_artwork_hash !== authoritativeOriginal.artwork_hash
              || version.original_design_hash !== authoritativeOriginal.design_hash) continue;
            try {
              const resolved = await window.SpiritInkSchoolLogo.validateDerivative({
                magic_preview_session_id: state.session_id,
                iteration: Math.max(1, Number(version.iteration) || 1),
                design_id: version.design_id,
                design_hash: version.design_hash,
                artwork_url: version.artwork_url,
                artwork_hash: version.artwork_hash,
                artwork_geometry_version: version.artwork_geometry_version,
              });
              const identity = commerce.normalizeGeneratedArtworkIdentity(resolved);
              if (resolved.source_kind === "school_logo"
                && resolved.school_logo_mode === "ai_customised"
                && resolved.provider_calls === 0
                && resolved.original_artwork_hash === authoritativeOriginal.artwork_hash
                && resolved.original_design_hash === authoritativeOriginal.design_hash
                && identityMatches(version, identity)) {
                validatedSchoolVersions.push(version);
              }
            } catch {
              // A saved derivative that cannot be re-authorized is discarded.
            }
          }
        }
        const retainedNormalVersions = record.versions.filter((version) => version.source_kind !== "school_logo");
        record.versions = [...retainedNormalVersions, ...validatedSchoolVersions];
        if (!validatedSchoolVersions.length) {
          discardStaleSchoolLogoVersions(record);
          record.design_id = "school-logo";
        } else {
          const restoredIndex = record.versions.indexOf(restoredActiveVersion);
          record.active_version = restoredIndex >= 0 ? restoredIndex : record.versions.length - 1;
          const active = record.active_version >= 0 ? record.versions[record.active_version] : null;
          record.school_logo_mode = active?.school_logo_mode === "ai_customised" ? "ai" : "original";
        }
      };
      for (const side of SIDES) await revalidateSchoolLogoVersions(state.sides[side]);
      elements.initialPrompt.value = state.initial_prompt;
      elements.refinePrompt.value = state.refine_prompt;

      selectedCategory = catalog.categories.find((category) => category.category_id === state.category_id) || null;
      selectedDesign = availableDesigns().find((design) =>
        design.id === state.design_id
        && design.enabled
        && design.generation_supported === true) || null;
      renderCategories();
      if (selectedCategory) {
        categoryColours = catalog.items.filter((item) => item.enabled && item.category_id === selectedCategory.category_id);
        selectedItem = categoryColours.find((item) => item.id === state.item_id)
          || categoryColours.find((item) => item.colour_id === selectedCategory.representative_colour_id)
          || categoryColours[0];
        renderSelectedItem();
        renderDesigns();
      }
      if (selectedDesign && selectedItem) renderStartingPreview();
      // Returning to the shop starts the journey at step 1. Saved versions are
      // kept, but the customer is never dropped back into the middle of a flow
      // they cannot reverse out of.
      suppressHistory = true;
      window.history.replaceState({ mpStep: "product" }, "");
      suppressHistory = false;
      setView("product");
      if (window.matchMedia("(pointer: coarse)").matches) {
        setText(elements.placementHint, "Drag to move. Pinch or drag a corner to resize.");
      }
      renderSideView();
      updateSummary();
      updateActions();
      setStatus(elements.catalogStatus, "", "");
      root.dataset.mpReady = "true";
      save();
      if (interruptedRequest) {
        setStatus(elements.builderStatus, "info", "A previous request was interrupted and was not sent again. You can safely try once more.");
      }
    }).catch((error) => {
      root.dataset.mpReady = "error";
      elements.categoryGrid.setAttribute("aria-busy", "false");
      setStatus(
        elements.catalogStatus,
        "error",
        customerErrorMessage(
          "catalogue load failed",
          error,
          "We couldn’t load the products. Please refresh the page to try again.",
        ),
      );
    });
  }

  const init = (scope = document) => scope.querySelectorAll("[data-mp-root]").forEach(initRoot);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
